import { createSignal, createEffect, onCleanup, onMount, untrack, on } from "solid-js";
import { Portal } from "solid-js/web";
import { sanitizeTooltipHtml } from '@utils/htmlSanitization';
import * as d3 from "d3";
import L from "leaflet";

import Loading from "../../utilities/Loading";

import { persistantStore } from "../../../store/persistantStore";
const { selectedClassIcon } = persistantStore;

import { filtered, tooltip, setTooltip, phase, color, normalized, eventType, tws, groupDisplayMode, tabledata } from "../../../store/globalStore";
import { MANEUVER_EVENT_TYPES } from "../../../utils/maneuverEventType";
import { selectedGroupKeys, setSelectedGroupKeys, selectedEvents, setSelectedEvents, setHasSelection, setSelection, triggerUpdate, setTriggerUpdate, triggerSelection, setTriggerSelection, isEventHidden } from "../../../store/selectionStore";
import { selectedGradesManeuvers, setSelectedGradesManeuvers, selectedStatesManeuvers, selectedRacesManeuvers, selectedLegsManeuvers, startDate, endDate, selectedSources } from "../../../store/filterStore";

import { putData } from "../../../utils/global";
import { error as logError, debug, log, warn } from "../../../utils/console";
import { normalizeManeuverStateFilterValues, parseManeuverStateFilterString } from "../../../utils/maneuverFoilingState";
import { buildColorGrouping, getManeuverGroupKeyFromFirstPoint, groupKeyEquals } from "../../../utils/colorGrouping";
import { assignYearEventMetadataOnPoint } from "../../../utils/maneuverMetadataFields";
import { fetchMapData } from "../../../services/maneuversDataService";
import { getManeuversConfig } from "../../../utils/maneuversConfig";
import { apiEndpoints } from "../../../config/env";
import { user } from "../../../store/userStore";
import { sourcesStore } from "../../../store/sourcesStore";
import { shouldBlockGradeLikeHotkeysForPerformanceSelections } from "../../../utils/performanceLocalSelection";
import { maneuverBaselineStore } from "../../../store/maneuverBaselineStore";
import { baselineTrackCssClass } from "../../../utils/maneuverBaselineTracks";
import type { ManeuverBaselineMapPoint } from "../../../utils/maneuverBaselineTracks";
import {
  collectFiniteLatLngs,
  positiveEventIdsFromList,
  positiveEventIdsFromTracks,
  notifyManeuverMapRecentered,
  shouldRecenterManeuverMap,
} from "../../../utils/maneuverMapRecenter";

import "../../../styles/thirdparty/mapbox-gl.css";
import "leaflet/dist/leaflet.css";

function escapeSelectorId(id: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(id);
  }
  return id.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

export interface ManeuverMapGroupedProps {
  context?: "dataset" | "historical" | "fleet";
  onDataUpdate?: () => void;
  instanceId?: string;
  /** Optional CSV or single value; may be a string or accessor (fleet/history parents). */
  filterYear?: string | (() => string);
  filterEvent?: string | (() => string);
  filterConfig?: string | (() => string);
  filterState?: string | (() => string);
  panelActive?: () => boolean;
  onLoadStateChange?: (loading: boolean) => void;
}

/** Point along a maneuver track (map + metadata). */
export interface MapTrackPoint {
  event_id?: number;
  time?: number;
  LatLng?: { lat: number; lng: number };
  tws_bin?: number;
  vmg_perc_avg?: number;
  tack?: string;
  State?: string;
  Config?: string;
  source_name?: string;
  race?: string | number;
  date?: string;
  hdg?: number;
  lat?: number;
  lng?: number;
  twa?: number;
  json?: { values: Record<string, unknown>[] };
  [key: string]: unknown;
}

type ManeuverPathRecord = {
  eventId: number;
  firstPoint: MapTrackPoint;
  points: MapTrackPoint[];
};

type LeafletMap = {
  getContainer(): HTMLElement;
  getPanes(): { overlayPane: HTMLElement };
  latLngToLayerPoint(latlng: { lat: number; lng: number }): { x: number; y: number };
  getZoom(): number;
  fitBounds(bounds: unknown): void;
  on(type: string, fn: (e: unknown) => void): unknown;
  once(type: string, fn: (e: unknown) => void): unknown;
  remove(): void;
  invalidateSize(): void;
  getBounds(): {
    getSouth(): number;
    getNorth(): number;
    getWest(): number;
    getEast(): number;
  };
  _loaded?: boolean;
  _container?: HTMLElement | null;
  _crosstrackListenersAttached?: boolean;
};

type GetItemColorFn = (item: MapTrackPoint) => string;

// Helper function to sanitize time values for use in CSS selectors
// Replaces problematic characters (negative signs, dots, etc.) with safe alternatives
// CSS identifiers must start with a letter, underscore, or non-ASCII character
// and can contain letters, digits, hyphens, underscores, and non-ASCII characters
/** Boat positions are drawn every 5 seconds along the track */
const BOAT_INTERVAL_SEC = 5;

/** Uniform sample count for grouped map averages (same resolution per color group). */
const MAP_GROUPED_RESAMPLE_POINTS = 96;

/** Returns [prevPoint, point] pairs for drawing boats at most every BOAT_INTERVAL_SEC, based on point.time (seconds). */
function getBoatPositionsByTime(points: MapTrackPoint[]): [MapTrackPoint, MapTrackPoint][] {
  const result: [MapTrackPoint, MapTrackPoint][] = [];
  if (!points?.length) return result;
  let lastBoatTime = -Infinity;
  for (let idx = 0; idx < points.length; idx++) {
    const d = points[idx];
    const t = typeof d?.time === "number" && Number.isFinite(d.time) ? d.time : (d?.time != null ? Number(d.time) : NaN);
    const timeSec = Number.isFinite(t) ? t : idx * 0.5; // fallback ~2Hz if time missing
    if (idx === 0 || timeSec >= lastBoatTime + BOAT_INTERVAL_SEC) {
      const prev = idx > 0 ? points[idx - 1] : d;
      result.push([prev, d]);
      lastBoatTime = timeSec;
    }
  }
  if (points.length > 0) {
    const last = points[points.length - 1];
    const secondLast = points.length > 1 ? points[points.length - 2] : last;
    if (result.length === 0 || result[result.length - 1][1] !== last) {
      result.push([secondLast, last]);
    }
  }
  return result;
}

const sanitizeTimeForId = (time: string | number | null | undefined): string => {
  // Handle null, undefined, or empty values
  if (time === null || time === undefined || time === '') {
    return '0';
  }
  
  const timeStr = String(time);
  
  // If the string is empty after conversion, return '0'
  if (timeStr.trim() === '') {
    return '0';
  }
  
  // Replace negative sign at the start with 'neg' prefix
  let sanitized = timeStr.startsWith('-') 
    ? 'neg' + timeStr.substring(1)
    : timeStr;
  
  // Replace dots with 'dot'
  sanitized = sanitized.replace(/\./g, 'dot');
  
  // Replace any remaining problematic characters (non-alphanumeric except underscore and hyphen) with underscore
  // But ensure we don't have consecutive hyphens or hyphens at the start
  sanitized = sanitized.replace(/[^a-zA-Z0-9_-]/g, '_');
  
  // Ensure it starts with a letter or underscore (CSS identifier requirement)
  // If it starts with a digit, prefix with 't'
  if (/^[0-9]/.test(sanitized)) {
    sanitized = 't' + sanitized;
  }
  
  // Replace consecutive hyphens with single hyphen
  sanitized = sanitized.replace(/-+/g, '-');
  
  // Remove leading/trailing hyphens
  sanitized = sanitized.replace(/^-+|-+$/g, '');
  
  // If empty after all processing, return '0'
  if (sanitized === '') {
    return '0';
  }
  
  return sanitized;
};

/** Mean heading in degrees [0, 360); falls back to first sample when vectors cancel. */
function circularMeanHeadingDegrees(anglesDeg: number[]): number | null {
  const valid = anglesDeg.filter((a) => typeof a === 'number' && Number.isFinite(a));
  if (valid.length === 0) return null;
  let sumCos = 0;
  let sumSin = 0;
  for (const deg of valid) {
    const rad = (((deg % 360) + 360) % 360) * (Math.PI / 180);
    sumCos += Math.cos(rad);
    sumSin += Math.sin(rad);
  }
  const len = Math.hypot(sumCos, sumSin);
  if (len < 1e-6) return valid[0] ?? null;
  let outDeg = (Math.atan2(sumSin, sumCos) * 180) / Math.PI;
  if (outDeg < 0) outDeg += 360;
  return outDeg;
}

function arithmeticMean(nums: number[]): number | undefined {
  const v = nums.filter((n) => typeof n === 'number' && Number.isFinite(n));
  if (v.length === 0) return undefined;
  return v.reduce((a, b) => a + b, 0) / v.length;
}

function isValidMapTrackPoint(p: MapTrackPoint): boolean {
  if (!p?.LatLng) return false;
  const lat = p.LatLng.lat;
  const lng = p.LatLng.lng;
  return Number.isFinite(lat) && Number.isFinite(lng);
}

interface SampledTrackPoint {
  lat: number;
  lng: number;
  time?: number;
  hdg?: number;
  twa?: number;
  vmg?: number;
  meta: MapTrackPoint;
}

/**
 * Linearly sample a time-sorted track at parametric position u in [0,1] (by sample index). Sampling every
 * track at the same u lets us average across ALL tracks at every output point, so the averaged polyline has no
 * kink where individual maneuvers of different lengths would otherwise drop out of the average.
 */
function sampleTrackAtParam(track: MapTrackPoint[], u: number): SampledTrackPoint | null {
  const k = track.length;
  if (k === 0) return null;
  const clampU = u < 0 ? 0 : u > 1 ? 1 : u;
  const f = clampU * (k - 1);
  const lo = Math.max(0, Math.min(k - 1, Math.floor(f)));
  const hi = Math.min(k - 1, lo + 1);
  const frac = f - lo;
  const a = track[lo];
  const b = track[hi];
  const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  const getLat = (p: MapTrackPoint) => num(p.lat) ?? p.LatLng!.lat;
  const getLng = (p: MapTrackPoint) => num(p.lng) ?? p.LatLng!.lng;
  const lerp = (x: number, y: number) => x + (y - x) * frac;
  const lerpOpt = (x?: number, y?: number): number | undefined => {
    if (x === undefined && y === undefined) return undefined;
    if (x === undefined) return y;
    if (y === undefined) return x;
    return x + (y - x) * frac;
  };
  return {
    lat: lerp(getLat(a), getLat(b)),
    lng: lerp(getLng(a), getLng(b)),
    time: lerpOpt(num(a.time), num(b.time)),
    hdg: lerpOpt(num(a.hdg), num(b.hdg)),
    twa: lerpOpt(num(a.twa), num(b.twa)),
    vmg: lerpOpt(num(a.vmg_perc_avg), num(b.vmg_perc_avg)),
    meta: a,
  };
}

/**
 * One averaged polyline per color group. Each track is resampled onto a shared parametric grid (u in [0,1]
 * across its full extent) and averaged across all tracks at each u. Because every track contributes to every
 * output point, the averaged polyline spans each group's full maneuver with no kink where shorter maneuvers
 * would otherwise drop out of an index-aligned average.
 */
function groupAndAverageMapTracks(tracks: MapTrackPoint[][], colorMode: string): MapTrackPoint[][] {
  const mode = String(colorMode || 'TWS').toUpperCase();
  const byColor = new Map<string | number, MapTrackPoint[][]>();
  for (const track of tracks) {
    if (!track?.length) continue;
    const g = getManeuverGroupKeyFromFirstPoint(track[0] as Record<string, unknown>, mode);
    if (g === undefined || g === null || (g as string) === '') continue;
    if (!byColor.has(g)) byColor.set(g, []);
    byColor.get(g)!.push(track);
  }
  const sortedKeys = Array.from(byColor.keys()).sort((a, b) => String(a).localeCompare(String(b)));
  const result: MapTrackPoint[][] = [];
  let keyIndex = 0;
  for (const gkey of sortedKeys) {
    keyIndex += 1;
    const groupTracks = byColor.get(gkey)!;
    const n = groupTracks.length;
    if (n === 0) continue;
    const syntheticEventId = -(100000 + keyIndex);

    const sortedSeries = groupTracks
      .map((tr) =>
        [...tr]
          .filter(isValidMapTrackPoint)
          .sort((a, b) => {
            const ta = typeof a.time === 'number' && Number.isFinite(a.time) ? a.time : Number(a.time);
            const tb = typeof b.time === 'number' && Number.isFinite(b.time) ? b.time : Number(b.time);
            return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
          })
      )
      .filter((tr) => tr.length > 0);

    if (sortedSeries.length === 0) continue;
    if (sortedSeries.length === 1) {
      const one = sortedSeries[0];
      if (one.length >= 2) result.push(one);
      continue;
    }

    // Resample every track onto a shared parametric grid (u in [0,1] across each track's full extent)
    // and average across ALL tracks at each u. Because every track contributes to every output point,
    // there is no kink where shorter maneuvers would otherwise drop out of an index-aligned average,
    // and the polyline still spans each group's full maneuver.
    const sampleCount = Math.max(...sortedSeries.map((tr) => tr.length));
    if (sampleCount < 2) {
      const first = groupTracks[0];
      if (first && first.length >= 2) result.push(first);
      continue;
    }

    const mergedPoints: MapTrackPoint[] = [];
    for (let j = 0; j < sampleCount; j++) {
      const u = j / (sampleCount - 1);
      const samples = sortedSeries
        .map((tr) => sampleTrackAtParam(tr, u))
        .filter((s): s is SampledTrackPoint => s !== null);
      if (samples.length === 0) continue;
      const meta = samples[0].meta;
      const meanLat = arithmeticMean(samples.map((s) => s.lat));
      const meanLng = arithmeticMean(samples.map((s) => s.lng));
      if (meanLat === undefined || meanLng === undefined) continue;
      const tVals = samples.map((s) => s.time).filter((t): t is number => t !== undefined);
      const tMean = arithmeticMean(tVals);
      const tOut = tMean !== undefined ? tMean : j;
      const hdgs = samples.map((s) => s.hdg).filter((h): h is number => h !== undefined);
      const twas = samples.map((s) => s.twa).filter((h): h is number => h !== undefined);
      const vmgs = samples.map((s) => s.vmg).filter((h): h is number => h !== undefined);
      const circHdg = circularMeanHeadingDegrees(hdgs);
      const linearHdg = arithmeticMean(hdgs);
      const hdgOut = circHdg !== null ? circHdg : linearHdg;
      // Average TWA by magnitude: a color group can mix port (negative) and starboard (positive)
      // maneuvers, and a signed mean would cancel them to a meaningless near-zero value.
      const twaOut = arithmeticMean(twas.map((t) => Math.abs(t)));
      const vmgOut = arithmeticMean(vmgs);
      const out: MapTrackPoint = {
        ...meta,
        event_id: syntheticEventId,
        time: parseFloat(Number(tOut).toFixed(10)),
        lat: meanLat,
        lng: meanLng,
        LatLng: new L.LatLng(meanLng, meanLat),
        hdg: hdgOut !== undefined && Number.isFinite(hdgOut) ? hdgOut : meta.hdg,
        twa: twaOut !== undefined ? twaOut : meta.twa,
        vmg_perc_avg: vmgOut !== undefined ? vmgOut : meta.vmg_perc_avg,
      };
      mergedPoints.push(out);
    }

    if (mergedPoints.length >= 2) {
      result.push(mergedPoints);
    } else if (groupTracks.length > 0) {
      const first = groupTracks[0];
      if (first && first.length >= 2) result.push(first);
    }
  }
  return result;
}

// Function to get boat path based on class icon type and zoom level
const getBoatPathForZoom = (zoomLevel: number): string => {
  const iconValue = selectedClassIcon();
  const iconType = (iconValue && typeof iconValue === 'string' && iconValue.trim() !== '') 
    ? iconValue.trim().toLowerCase() 
    : 'monohull';
  
  if (iconType === 'multihull') {
    // Multihull paths scaled for different zoom levels, centered at (0,0)
    if (zoomLevel > 18) {
      // Full size multihull (2x wider), centered at (0,0) - goes from y=-12 to y=12
      return "M0 12 L5.332 0 L5.332 6 L6.665 12 L8 6 L8 -12 L-8 -12 L-8 6 L-6.665 12 L-5.332 6 L-5.332 0 Z";
    } else if (zoomLevel > 14) {
      // Medium size multihull (58% scale), centered at (0,0)
      return "M0 7 L3.109 0 L3.109 3.5 L3.888 7 L4.666 3.5 L4.666 -7 L-4.666 -7 L-4.666 3.5 L-3.888 7 L-3.109 3.5 L-3.109 0 Z";
    } else if (zoomLevel > 10) {
      // Small size multihull (25% scale), centered at (0,0)
      return "M0 3 L1.333 0 L1.333 1.5 L1.666 3 L2 1.5 L2 -3 L-2 -3 L-2 1.5 L-1.666 3 L-1.333 1.5 L-1.333 0 Z";
    }
  }
  
  // Default monohull paths for different zoom levels
  if (zoomLevel > 18) {
    return "M0 -12 L-4 -12 L-4 0 L-2 8 L0 12 L2 8 L4 0 L4 -12 Z";
  } else if (zoomLevel > 14) {
    return "M0 -7 L-2 -7 L-2 0 L-1 4 L0 7 L1 4 L2 0 L2 -7 Z";
  } else if (zoomLevel > 10) {
    return "M0 -3 L-1 -3 L-1 0 L-0.5 1.5 L0 3 L0.5 1.5 L1 0 L1 -3 Z";
  }
  
  return ""; // Empty path for very low zoom
};

export default function ManeuverMap(props?: ManeuverMapGroupedProps) {
  // Get context from props, default to 'dataset' for backward compatibility
  const context = props?.context || 'dataset';
  const maneuverMapElementId = () =>
    props?.instanceId ? `${props.instanceId}-maneuver-map` : "maneuver-map";
  const d3MapPane = () => d3.select(`#${escapeSelectorId(maneuverMapElementId())}`);
  void getManeuversConfig(context);
  const [mapdata, setMapData] = createSignal<MapTrackPoint[][]>([]);
  type ColorGroup = { key: string | number; items: MapTrackPoint[] };
  const [, setColorGroups] = createSignal<ColorGroup[]>([]);
  const [, setGroupRepIds] = createSignal<Set<number>>(new Set<number>());
  const [description, setDescription] = createSignal("");
  const [loading, setLoading] = createSignal(true);
  const [firstload, setFirstLoad] = createSignal(true);
  const [mapInitialized, setMapInitialized] = createSignal(false);

  const isPanelActive = (): boolean => {
    if (!props) return false;
    const fn = props.panelActive;
    return fn ? fn() : true;
  };

  createEffect(() => {
    if (!props || !isPanelActive()) return;
    props.onLoadStateChange?.(loading());
  });

  let map: LeafletMap | null = null;
  /** D3 selections from Leaflet overlay; typed loosely — strict D3 generics don't match overlay parent typing. */
  let svg: d3.Selection<SVGSVGElement, unknown, any, any> | undefined;
  let g: d3.Selection<SVGGElement, unknown, any, any> | undefined;
  let updateInProgress = false;
  let lastFilteredCount = 0;
  let filteredWatcherTriggered = false;
  let zoom_level = 13;
  let latlngarray: { lat: number; lng: number }[] = [];
  let firstDraw = true;
  let lastEventType: string | null = null;
  let lastPhase: string | null = null;
  let shouldCenterOnNextDraw = false;
  /** Event ids on screen when the maneuver type changed. A draw of only these must not fit. */
  let recenterStaleEventIds: Set<number> | null = null;
  let centerToken = 0;
  let centerTimeout: ReturnType<typeof setTimeout> | null = null;
  // Track all boat fade timeouts so we can clear them on phase change
  let boatFadeTimeouts: NodeJS.Timeout[] = [];

  let mapWidth = 910;
  let mapHeight = 600; // Default height, will be updated to fill container
  let maneuverPathsData: ManeuverPathRecord[] = [];

  // Using global color scale for consistency
  let myColorScale: any = d3.scaleLinear();
  let getItemColor: GetItemColorFn | null = null; // Store getItemColor function for SOURCE coloring
  let minVal = 9999999
  let maxVal = -9999999

  const isFiltered = (id: number): boolean => (filtered() as unknown[]).includes(id);
  const getPathGroupKey = (firstPoint: MapTrackPoint | null | undefined) =>
    firstPoint ? getManeuverGroupKeyFromFirstPoint(firstPoint as Record<string, unknown>, color()) : null;
  const isPathSelected = (firstPoint: MapTrackPoint | null | undefined) => {
    const pathKey = getPathGroupKey(firstPoint);
    return pathKey != null && selectedGroupKeys().some((k) => groupKeyEquals(k, pathKey));
  };

  const getDescription = () => {
    const currentPhase = phase();
    const isNormalized = normalized();
    
    if (currentPhase == 'FULL') {
      if (isNormalized) {
        setDescription('0_Normalized')
      } else {
        setDescription('0_Standard')
      }
    } else if (currentPhase == 'INVESTMENT') {
      if (isNormalized) {
        setDescription('1_Normalized')
      } else {
        setDescription('1_Standard')
      }
    } else if (currentPhase == 'TURN') {
      if (isNormalized) {
        setDescription('2_Normalized')
      } else {
        setDescription('2_Standard')
      }
    } else if (currentPhase == 'ACCELERATION') {
      if (isNormalized) {
        setDescription('3_Normalized')
      } else {
        setDescription('3_Standard')
      }
    } else {
      // Default fallback - use FULL phase description if phase is not set or unknown
      if (isNormalized) {
        setDescription('0_Normalized')
      } else {
        setDescription('0_Standard')
      }
    }
  }

  // Async function to fetch data
  const fetchData = async () => {
    try {
      // Get the current filtered event list - map endpoint requires non-empty event_list
      const currentFiltered = filtered();
      const hasEvents = currentFiltered && Array.isArray(currentFiltered) && currentFiltered.length > 0;
      
      log(`[Map] fetchData called - filtered() has ${currentFiltered?.length || 0} event_ids, hasEvents: ${hasEvents}`);
      
      // Map endpoint requires event_list to be non-empty, so we must have events
      if (!hasEvents) {
        // No events yet (e.g. table still loading or no events selected) – skip map fetch for now
        // Only log if map is initialized (to avoid spam during initial load)
        if (mapInitialized() && map) {
          log('Map: Skipping fetch - no events in filtered list');
        }
        return;
      }
      
      // Validate dataset_id when required (for dataset context, we still want to validate dataset_id exists)
      if (context === 'dataset') {
        const { selectedDatasetId } = persistantStore;
        const datasetId = selectedDatasetId();
        if (!datasetId || datasetId <= 0) {
          logError('Map: Cannot fetch data - invalid dataset_id:', datasetId);
          return;
        }
      }
      
      // Ensure description is set before making API call
      getDescription();
      const desc = description();
      
      // Validate that we have a description before making the API call
      if (!desc || desc.trim() === '') {
        logError('Map: Cannot fetch data - description is not set. Phase:', phase());
        return;
      }
      
      // filtered() contains event IDs (numbers) - extract them safely
      const eventIds: number[] = currentFiltered.map((item: any) => {
        // Handle both cases: if item is a number, return it; if it's an object, extract event_id
        return typeof item === 'number' ? item : (item?.event_id ?? item);
      }).filter((id: any): id is number => typeof id === 'number' && id > 0);

      if (eventIds.length === 0) {
        debug('Map: No valid event IDs found in filtered list');
        return;
      }

      // Use time range only when many events and NOT fleet. For fleet, always pass eventList so map data matches table (service batches in chunks of 100).
      const useTimeRange = eventIds.length > 100 && context !== 'fleet';
      log(`[Map] fetchData - eventIds.length = ${eventIds.length}, useTimeRange = ${useTimeRange}, context = ${context}`);
      
      let fetchParams: any = {
        eventType: eventType(),
        description: desc,
      };

      if (useTimeRange) {
        log(`[Map] fetchData - Using time range endpoint because eventIds.length = ${eventIds.length}`);
        
        // Extract time range from filterStore or use selectedDate
        const { selectedDate } = persistantStore;
        const filterStoreStart = startDate();
        const filterStoreEnd = endDate();
        const date = selectedDate();
        
        let startDateValue: string;
        let endDateValue: string;
        
        if (filterStoreStart && filterStoreEnd) {
          startDateValue = filterStoreStart;
          endDateValue = filterStoreEnd;
        } else if (date) {
          startDateValue = date;
          endDateValue = date;
        } else {
          // Fallback to wide range - always provide dates when using by-range endpoint
          startDateValue = '2020-01-01';
          endDateValue = '2099-12-31';
        }

        fetchParams.timeRange = {
          startDate: startDateValue,
          endDate: endDateValue
        };

        // Extract filters from filterStore and props
        const filters: any = {};
        
        // GRADE filter - CRITICAL: This must match the table's GRADE filter
        // Fleet maneuvers pages use local selectedGrades (not synced to filterStore) with default [2, 3]
        // So we need to use default [2, 3] if filterStore is empty to match table behavior
        let grades = selectedGradesManeuvers();
        log(`[Map] fetchData - selectedGradesManeuvers() from filterStore: ${JSON.stringify(grades)}, length: ${grades.length}`);
        
        // If filterStore grades are empty, use default [2, 3] to match FleetManeuvers / fleet history table default
        if (!grades || grades.length === 0) {
          grades = ['2', '3']; // Default to match table's default
          log(`[Map] fetchData - Using default grades [2, 3] to match table behavior`);
        }
        
        if (grades && grades.length > 0) {
          const gradeNumbers = grades.map((g: any) => Number(g)).filter((n: number) => !isNaN(n));
          if (gradeNumbers.length > 0) {
            filters.GRADE = gradeNumbers;
            log(`[Map] fetchData - Added GRADE filter: ${JSON.stringify(filters.GRADE)}`);
          } else {
            log(`[Map] fetchData - WARNING: selectedGradesManeuvers() had values but none converted to valid numbers`);
          }
        }

        // YEAR filter (from props)
        const filterYearValue = props?.filterYear ? (typeof props.filterYear === 'function' ? props.filterYear() : props.filterYear) : '';
        log(`[Map] fetchData - filterYear prop value: "${filterYearValue}"`);
        if (filterYearValue && filterYearValue.trim()) {
          const years = filterYearValue.split(',').map((y: string) => parseInt(y.trim(), 10)).filter((y: number) => !isNaN(y));
          if (years.length > 0) {
            filters.YEAR = years;
            log(`[Map] fetchData - Added YEAR filter: ${JSON.stringify(filters.YEAR)}`);
          }
        }

        // EVENT filter (from props)
        const filterEventValue = props?.filterEvent ? (typeof props.filterEvent === 'function' ? props.filterEvent() : props.filterEvent) : '';
        log(`[Map] fetchData - filterEvent prop value: "${filterEventValue}"`);
        if (filterEventValue && filterEventValue.trim()) {
          const events = filterEventValue.split(',').map((e: string) => e.trim()).filter((e: string) => e.length > 0);
          if (events.length > 0) {
            filters.EVENT = events;
            log(`[Map] fetchData - Added EVENT filter: ${JSON.stringify(filters.EVENT)}`);
          }
        }

        // CONFIG filter (from props)
        const filterConfigValue = props?.filterConfig ? (typeof props.filterConfig === 'function' ? props.filterConfig() : props.filterConfig) : '';
        log(`[Map] fetchData - filterConfig prop value: "${filterConfigValue}"`);
        if (filterConfigValue && filterConfigValue.trim()) {
          const configs = filterConfigValue.split(',').map((c: string) => c.trim()).filter((c: string) => c.length > 0);
          if (configs.length > 0) {
            filters.CONFIG = configs;
            log(`[Map] fetchData - Added CONFIG filter: ${JSON.stringify(filters.CONFIG)}`);
          }
        }

        // STATE filter (from props first, then filterStore as fallback)
        const filterStateValue = props?.filterState ? (typeof props.filterState === 'function' ? props.filterState() : props.filterState) : '';
        log(`[Map] fetchData - filterState prop value: "${filterStateValue}"`);
        if (filterStateValue && filterStateValue.trim()) {
          const states = parseManeuverStateFilterString(filterStateValue);
          if (states.length > 0) {
            filters.STATE = states;
            log(`[Map] fetchData - Added STATE filter from props: ${JSON.stringify(filters.STATE)}`);
          }
        } else {
          // Fallback to filterStore STATE filter
          const states = selectedStatesManeuvers();
          log(`[Map] fetchData - selectedStatesManeuvers() from filterStore: ${JSON.stringify(states)}`);
          if (states.length > 0) {
            filters.STATE = normalizeManeuverStateFilterValues(states);
            log(`[Map] fetchData - Added STATE filter from filterStore: ${JSON.stringify(filters.STATE)}`);
          }
        }

        // SOURCE_NAME filter for fleet context - pass to API via sourceNames parameter
        // Use props.context so TS does not narrow away "fleet" inside the useTimeRange branch.
        if ((props?.context ?? "dataset") === "fleet") {
          const selectedSourceNames = selectedSources(); // filterStore returns string[] of source names
          if (selectedSourceNames && Array.isArray(selectedSourceNames) && selectedSourceNames.length > 0) {
            fetchParams.sourceNames = selectedSourceNames;
            log(`[Map] fetchData - Passing sourceNames to API: ${JSON.stringify(selectedSourceNames)}`);
          } else {
            // Fallback: if no sources selected, use all available sources from sourcesStore
            if (sourcesStore.isReady()) {
              const allSources = sourcesStore.sources();
              if (allSources && allSources.length > 0) {
                fetchParams.sourceNames = allSources.map((s: any) => s.source_name).filter((name: string) => name);
                log(`[Map] fetchData - No sources selected, using all available sources: ${JSON.stringify(fetchParams.sourceNames)}`);
              } else {
                warn(`[Map] fetchData - No sources available in sourcesStore for fleet context`);
              }
            } else {
              warn(`[Map] fetchData - sourcesStore not ready, cannot get source names for fleet context`);
            }
          }
        }

        // RACE filter (convert to YEAR/EVENT if needed, or use as-is)
        const races = selectedRacesManeuvers();
        if (races.length > 0) {
          // For now, we'll need to extract YEAR and EVENT from the data
          // But since we don't have access to the full data here, we'll skip RACE filter
          // The parent component should handle this
        }

        // LEG filter - not directly supported by by-range endpoint, skip for now

        if (Object.keys(filters).length > 0) {
          fetchParams.filters = filters;
          log(`[Map] fetchData - Built filters: ${JSON.stringify(filters)}`);
        } else {
          log(`[Map] fetchData - No filters built (empty filters object)`);
        }

        // For historical context, we need to get source names from the selected source
        if (context === 'historical') {
          const { selectedSourceId } = persistantStore;
          const sourceId = selectedSourceId();
          if (sourceId && sourceId > 0) {
            // Get source name from sourcesStore
            const sources = sourcesStore.sources();
            const source = sources.find((s: any) => s.source_id === sourceId);
            if (source && source.source_name) {
              fetchParams.sourceNames = [source.source_name];
            }
          }
        }
        
        // IMPORTANT: Do NOT include eventList when using time range endpoint
        // This ensures we use the by-range endpoint
        // Explicitly set to undefined to prevent any accidental usage
        fetchParams.eventList = undefined;
      } else {
        log(`[Map] fetchData - Using regular event_list endpoint because eventIds.length = ${eventIds.length}`);
        // Use regular event_list endpoint
        fetchParams.eventList = eventIds;
        // Explicitly do NOT set timeRange when using event_list
        fetchParams.timeRange = undefined;
      }

      const json_data = await fetchMapData(context, fetchParams);

      if (json_data && json_data.length > 0) {
        latlngarray = [];
        const dataArray: MapTrackPoint[][] = [];
        
        // Log map data counts for debugging
        const uniqueEventIds = new Set(json_data.map((d: any) => d.event_id));
        log(`[Map] fetchData - API returned ${json_data.length} records, ${uniqueEventIds.size} unique event_ids`);
        
        // Start with all data from API - apply client-side filters to match table exactly
        // Note: SOURCE_NAME filtering is now done server-side via sourceNames parameter
        let filtered_data = json_data;
        
        // Apply TWS filter (same logic as FleetManeuvers.tsx / fleet history filterData)
        const selectedTws = Number(tws());
        if (!isNaN(selectedTws)) {
          const beforeTwsCount = filtered_data.length;
          filtered_data = filtered_data.filter((item: any) => {
            const itemTws = item.tws_bin ?? item.tws_avg;
            if (itemTws === null || itemTws === undefined || isNaN(Number(itemTws))) {
              return false;
            }
            const itemTwsNum = Number(itemTws);
            // Filter within ±2.5 of the selected bin value
            // Bin 10 includes values from 7.5 to 12.5 (exclusive on upper bound to avoid overlap)
            const minTws = selectedTws - 2.5;
            const maxTws = selectedTws + 2.5;
            return itemTwsNum >= minTws && itemTwsNum < maxTws;
          });
          const afterTwsCount = filtered_data.length;
          log(`[Map] fetchData - After TWS filter (${selectedTws}): ${beforeTwsCount} -> ${afterTwsCount} records`);
        } else {
          log(`[Map] fetchData - No TWS filter applied (tws() is not a valid number: ${tws()})`);
        }
        
        // Apply RACE filter (same logic as FleetManeuvers.tsx / fleet history filterData)
        const races = selectedRacesManeuvers();
        if (races.length > 0) {
          const beforeRaceCount = filtered_data.length;
          filtered_data = filtered_data.filter((item: any) => {
            const raceValue = item.Race_number ?? item.race_number ?? item.race ?? item.Race;
            if (raceValue == null || raceValue === undefined) return false;
            // Handle TRAINING case
            if (raceValue === -1 || raceValue === '-1') {
              return races.includes('TRAINING') || races.includes('training');
            }
            return races.some(selectedRace => {
              if (selectedRace === 'TRAINING' || selectedRace === 'training') return false;
              return Number(raceValue) === Number(selectedRace);
            });
          });
          const afterRaceCount = filtered_data.length;
          log(`[Map] fetchData - After RACE filter: ${beforeRaceCount} -> ${afterRaceCount} records`);
        }
        
        // Apply LEG filter (same logic as FleetManeuvers.tsx / fleet history filterData)
        const legs = selectedLegsManeuvers();
        if (legs.length > 0) {
          const beforeLegCount = filtered_data.length;
          filtered_data = filtered_data.filter((item: any) => {
            const legValue = item.leg_number ?? item.Leg_number ?? item.LEG;
            if (legValue == null || legValue === undefined) return false;
            return legs.some((leg: string) => Number(leg) === Number(legValue));
          });
          const afterLegCount = filtered_data.length;
          log(`[Map] fetchData - After LEG filter: ${beforeLegCount} -> ${afterLegCount} records`);
        }
        
        // Apply isFiltered() check as final validation to ensure consistency with table (exclude hidden events)
        const beforeIsFilteredCount = filtered_data.length;
        filtered_data = filtered_data.filter(function (d: { event_id: number }) {
          return isFiltered(d.event_id) && !isEventHidden(d.event_id);
        });
        const afterIsFilteredCount = filtered_data.length;
        
        const filteredUniqueEventIds = new Set(filtered_data.map((d: any) => d.event_id));
        log(`[Map] fetchData - After isFiltered check: ${beforeIsFilteredCount} -> ${afterIsFilteredCount} records, ${filteredUniqueEventIds.size} unique event_ids`);
        
        // Also log what filtered() contains for comparison
        const currentFiltered = filtered();
        log(`[Map] fetchData - filtered() contains ${currentFiltered.length} event_ids`);

        const orderRank = new Map(eventIds.map((id, i) => [id, i]));
        filtered_data.sort(
          (a: { event_id: number }, b: { event_id: number }) =>
            (orderRank.get(a.event_id) ?? 0) - (orderRank.get(b.event_id) ?? 0)
        );

        const tableByEventId = new Map<number, Record<string, unknown>>();
        for (const row of tabledata() as Record<string, unknown>[]) {
          const eid = row.event_id;
          if (typeof eid === "number") tableByEventId.set(eid, row);
        }

        const evt = (eventType() || '').toUpperCase();
        const usePortStbd = evt === 'TAKEOFF' || evt === 'BEARAWAY' || evt === 'ROUNDUP';
        filtered_data.forEach((item) => {
          const tableRow = tableByEventId.get(item.event_id);
          const processedData = item.json.values.map((d: Record<string, string | undefined>) => {
            let tackValue: string | undefined;
            if (usePortStbd) {
              const twa = item.twa_entry ?? item.twa_build ?? item.Twa_start ?? 0;
              tackValue = Number(twa) > 0 ? 'STBD' : 'PORT';
            } else if (phase() == 'FULL' || phase() == 'TURN') {
              tackValue = item.twa_entry > 0 ? 'S - P' : 'P - S';
            } else if (phase() == 'INVESTMENT') {
              tackValue = item.twa_entry > 0 ? 'STBD' : 'PORT';
            } else {
              tackValue = item.twa_entry > 0 ? 'PORT' : 'STBD';
            }

            // Convert race_number -1 to 'TRAINING'
            const raceValue = item.Race_number ?? item.race_number ?? item.race ?? item.Race;
            const convertedRace = (raceValue === -1 || raceValue === '-1') ? 'TRAINING' : raceValue;

            const newItem: MapTrackPoint = {
              event_id: item.event_id,
              tws_bin: item.tws_bin,
              tack: tackValue,
              vmg_perc_avg: item.vmg_perc_avg,
              race: convertedRace,
              source_name: item.source_name || '',
              State: item.State,
              Config: item.Config,
              time: parseFloat(String(d.time)),
              hdg: parseFloat(String(d.hdg)),
              lat: parseFloat(String(d.lat)),
              lng: parseFloat(String(d.lng)),
              twa: parseFloat(String(d.twa)),
              LatLng: new L.LatLng(parseFloat(String(d.lng)), parseFloat(String(d.lat))),
            };
            assignYearEventMetadataOnPoint(
              newItem as Record<string, unknown>,
              item as Record<string, unknown>,
              tableRow
            );

            if (newItem.LatLng) latlngarray.push(newItem.LatLng);
            return newItem; 
          });
        
          dataArray.push(processedData); 
        });

        setMapData(dataArray);
        // Flatten to points and build color grouping for downstream consumers
        const flat = dataArray.flat();
        const { groups } = buildColorGrouping(flat, color());
        setColorGroups(groups);
        // Compute representative event_id per group for grouped rendering
        const reps = new Set<number>();
        groups.forEach((g: ColorGroup) => {
          const first = g.items && g.items.length > 0 ? g.items[0] : null;
          if (first && first.event_id !== undefined && first.event_id !== null) reps.add(first.event_id);
        });
        setGroupRepIds(reps);
        
        updateMinMaxRanges()

        return { dataArray, reps };
      } else {
        latlngarray = [];
        return { dataArray: [], reps: new Set<number>() };
      }
    } catch (error: any) {
      logError("Error fetching data:", error);
      return { dataArray: [], reps: new Set<number>() };
    }
  };

  function reinitMap() {
    try {
      if (!map) return;
      const mc = map.getContainer() as HTMLElement | null;
      const layoutEl =
        (mc && mc.closest(".maneuver-map-container")) ||
        d3.select("#map-area").node() ||
        mc;
      if (!layoutEl || !((layoutEl as HTMLElement).offsetWidth >= 0)) return;
      // Layout px, not getBoundingClientRect: #media-container scales the page with --scale-factor and
      // a post-transform read would feed a shrunken size back into layout (double scale).
      mapWidth = (layoutEl as HTMLElement).offsetWidth;
      mapHeight = (layoutEl as HTMLElement).offsetHeight;

      const root = mc?.closest(".maneuver-map-container");
      if (root) {
        d3.select(root)
          .style("width", "100%")
          .style("height", "100%");
        if (mc) d3.select(mc).style("width", "100%").style("height", "100%");
      } else {
        d3.selectAll(".maneuver-map-container")
          .style("width", "100%")
          .style("height", "100%");
        d3.selectAll(".maneuver-map")
          .style("width", "100%")
          .style("height", "100%");
      }

      svg = d3.select(map.getPanes().overlayPane).select("svg");
      svg.selectAll("g").remove();

      g = svg.append("g").attr("width", mapWidth).attr("height", mapHeight);
    } catch (error: any) {
      logError('🗺️ Map.jsx: reinitMap error', error);
    }
  }

  function initMap() {
    try {
      const elId = maneuverMapElementId();
      const maneuverMapElement = document.getElementById(elId);
      if (!maneuverMapElement) return;

      const layoutEl =
        maneuverMapElement.closest(".maneuver-map-container") ||
        d3.select("#map-area").node() ||
        maneuverMapElement;
      // Layout px — see reinitMap.
      mapWidth = (layoutEl as HTMLElement).offsetWidth;
      mapHeight = (layoutEl as HTMLElement).offsetHeight;

      const mapboxTiles = L.tileLayer('', {
        attribution: ''
      });

      const root = maneuverMapElement.closest(".maneuver-map-container");
      if (root) {
        d3.select(root)
          .style("width", "100%")
          .style("height", "100%");
        d3.select(maneuverMapElement)
          .style("width", "100%")
          .style("height", "100%");
      } else {
        d3.selectAll(".maneuver-map-container")
          .style("width", "100%")
          .style("height", "100%");
        d3.selectAll(".maneuver-map")
          .style("width", "100%")
          .style("height", "100%");
      }

      map = L.map(elId, { minZoom: 0, maxZoom: 24, zoomControl: true })
        .addLayer(mapboxTiles)
        .setView([0, 0], zoom_level) as unknown as LeafletMap;

      L.svg({ interactive: true }).addTo(map);

      svg = d3.select(map.getPanes().overlayPane).select("svg");

      svg.attr("pointer-events", "auto")
        .attr("z-index", 900);

      svg.selectAll("g").remove();
      g = svg.append("g").attr("width", mapWidth).attr("height", mapHeight)
        .attr("z-index", 1000);

      d3.selectAll(".leaflet-attribution-flag").remove();

      map.on("dblclick", function (_e: unknown) {
        centerMap(latlngarray);
      });
    } catch (error: any) {
      /* centering is best-effort */
    }
  }

  const toMapLinePath = d3
    .line<MapTrackPoint>()
    .defined(function (d) {
      if (!d || !d.LatLng) return false;
      if (!map) return false;
      try {
        const point = map.latLngToLayerPoint(d.LatLng);
        return point && !isNaN(point.x) && !isNaN(point.y) && isFinite(point.x) && isFinite(point.y);
      } catch {
        return false;
      }
    })
    .x(function (d) {
      if (!map || !d || !d.LatLng) return 0;
      try {
        const point = map.latLngToLayerPoint(d.LatLng);
        return isNaN(point.x) ? 0 : point.x;
      } catch {
        return 0;
      }
    })
    .y(function (d) {
      if (!map || !d || !d.LatLng) return 0;
      try {
        const point = map.latLngToLayerPoint(d.LatLng);
        return isNaN(point.y) ? 0 : point.y;
      } catch {
        return 0;
      }
    });

  function applyLatLngToLayer(d: { lat: number; lng: number }) {
    if (!map || !d) {
      return { x: 0, y: 0 };
    }
    try {
      const point = map.latLngToLayerPoint(d);
      if (isNaN(point.x) || isNaN(point.y) || !isFinite(point.x) || !isFinite(point.y)) {
        return { x: 0, y: 0 };
      }
      return point;
    } catch {
      return { x: 0, y: 0 };
    }
  }

  function centerMap(arr: { lat: number; lng: number }[]) {
    try {
      if (arr != undefined && arr.length > 0 && map) {
        const bounds = new L.LatLngBounds(arr);
        map.fitBounds(bounds);
      }
    } catch {
      // ignore invalid bounds
    }
  }

  function invalidatePendingRecenter(): void {
    centerToken += 1;
    if (centerTimeout != null) {
      clearTimeout(centerTimeout);
      centerTimeout = null;
    }
  }

  function scheduleRecenter(tracks: MapTrackPoint[][], sourceEventIds?: readonly number[]): void {
    const points = collectFiniteLatLngs(tracks);
    const drawnEventIds = sourceEventIds ?? positiveEventIdsFromTracks(tracks);
    const fit = shouldRecenterManeuverMap({
      shouldCenter: shouldCenterOnNextDraw,
      firstDraw,
      drawnEventIds,
      drawnPointCount: points.length,
      staleEventIds: recenterStaleEventIds,
    });
    if (!fit) {
      if ((shouldCenterOnNextDraw || firstDraw) && points.length > 0) {
        debug("Map: holding recenter until the new maneuver type is drawn", {
          drawnEvents: drawnEventIds.length,
          staleEvents: recenterStaleEventIds?.size ?? 0,
        });
      }
      return;
    }
    const token = centerToken;
    if (centerTimeout != null) clearTimeout(centerTimeout);
    centerTimeout = setTimeout(() => {
      centerTimeout = null;
      if (token !== centerToken || !map) return;
      const fittedNewType = recenterStaleEventIds != null;
      centerMap(points);
      shouldCenterOnNextDraw = false;
      recenterStaleEventIds = null;
      firstDraw = false;
      if (fittedNewType) notifyManeuverMapRecentered();
    }, 100);
  }

  // Capture the ids of the type that is about to be replaced. The selection clear refetches
  // the map before the new type's rows exist; fitting that draw would leave the next type off-screen.
  createEffect(
    on(
      () => (eventType() || "").toUpperCase(),
      (next, prev) => {
        if (prev == null || next === prev) return;
        invalidatePendingRecenter();
        shouldCenterOnNextDraw = true;
        const current = untrack(() => filtered());
        const ids = Array.isArray(current) ? positiveEventIdsFromList(current) : [];
        recenterStaleEventIds = new Set(ids);
        debug("Map: maneuver type changed; recenter once the new events are drawn", {
          from: prev,
          to: next,
          previousEventCount: ids.length,
        });
      },
      { defer: true }
    )
  );

  function updateMinMaxRanges() {
    let channel = 'tws_bin'
    if (color() == 'VMG') {
      channel = 'vmg_perc_avg'
    }

    minVal = 9999999
    maxVal = -9999999
    mapdata().forEach(function (item: MapTrackPoint[]) {
      try {
        const d = item[0];
        const val = parseFloat(String(d[channel as keyof MapTrackPoint] ?? ""));
        if (val > maxVal) {maxVal = val}
        if (val < minVal) {minVal = val}
      } catch {
        /* ignore non-numeric values */
      }
    })

    InitScales()  
  }

  function InitScales() {
    if (color() === 'TWS') //TWS
    {
        // Fixed TWS bins: 10, 15, 20, 25, 30, 35, 40, 45, 50
        // Use scaleThreshold with 8 thresholds to create 9 color ranges (one for each bin)
        myColorScale = d3.scaleThreshold();
        myColorScale.domain([12.5, 17.5, 22.5, 27.5, 32.5, 37.5, 42.5, 47.5]);
        myColorScale.range(["blue", "lightblue", "cyan", "lightgreen", "yellow", "orange", "red", "darkred", "purple"]);
    }
    else if (color() === 'VMG') //VMG
    {
        myColorScale = d3.scaleLinear();
        myColorScale.domain([minVal, (minVal + maxVal) / 2, maxVal]);
        myColorScale.range(["blue", "lightgrey", "red"]);
    }
    else if (color() === 'TACK') //TACK
    {
        myColorScale = d3.scaleThreshold();
        myColorScale.domain([-180, -1, 1, 180]);
        myColorScale.range(["red", "red", "#64ed64", "#64ed64"]);
    }
    else if (color() === 'MAINSAIL') //MAINSAIL
    {
      // Use buildColorGrouping for consistency with DataTable and Scatter
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'MAINSAIL');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'HEADSAIL') //HEADSAIL
    {
      // Use buildColorGrouping for consistency with DataTable and Scatter
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'HEADSAIL');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'RACE') //RACE
    {
      // Use buildColorGrouping for consistency with DataTable and Scatter
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'RACE');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'SOURCE') //SOURCE (fleet context)
    {
      // Use buildColorGrouping for SOURCE to get fleet colors
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'SOURCE');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'STATE') //STATE
    {
      // Use buildColorGrouping for consistency with DataTable and Scatter
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'STATE');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'CONFIG') //CONFIG
    {
      // Use buildColorGrouping for consistency with DataTable and Scatter
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'CONFIG');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'CREW') //CREW
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'CREW');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'TESTS')
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'TESTS');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'YEAR') //YEAR
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'YEAR');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else if (color() === 'EVENT') //EVENT
    {
      const data: MapTrackPoint[] = [];
      mapdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, 'EVENT');
      myColorScale = scale;
      getItemColor = getItemColorFn as GetItemColorFn;
    }
    else
    {
      myColorScale = d3.scaleLinear();
      //myColorScale.domain([4, 8, 14, 18, 22])
      myColorScale.domain([8, 16, 28, 36, 44]);
      myColorScale.range(["yellow", "orange", "red"]);
    }
  }

  // Get the original color based on current color-by setting (without selection-based coloring)
  function getOriginalColor(d: MapTrackPoint | undefined): string {
    if (color() === 'TWS') {
      if (d != undefined) {
        const val = d.tws_bin;
        return (myColorScale as (v: number) => string)(Number(val));
      }
      return "grey";
    }
    if (color() === 'VMG') {
      if (d != undefined) {
        const val = d.vmg_perc_avg;
        return (myColorScale as (v: number) => string)(Number(val));
      }
      return "grey";
    }
    if (color() === 'TACK') {
      if (d != undefined) {
        if (d.tack == 'PORT') {
          return "red";
        } else if (d.tack == 'STBD') {
          return "#64ed64";
        } else if (d.tack == 'P - S' || d.tack == 'P-S') {
          return "red";
        } else if (d.tack == 'S - P' || d.tack == 'S-P') {
          return "#64ed64";
        }
        return "#64ed64";
      }
      return "grey";
    }
    if (color() === 'MAINSAIL') {
      if (d != undefined && getItemColor) {
        // Use stored getItemColor function for consistent coloring
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'HEADSAIL') {
      if (d != undefined && getItemColor) {
        // Use stored getItemColor function for consistent coloring
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'RACE') {
      if (d != undefined && getItemColor) {
        // Use stored getItemColor function for consistent coloring
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'SOURCE') {
      if (d != undefined && getItemColor) {
        // Use stored getItemColor function for efficient coloring
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'STATE') {
      if (d != undefined && getItemColor) {
        // Use stored getItemColor function for consistent coloring
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'CONFIG') {
      if (d != undefined && getItemColor) {
        // Use stored getItemColor function for consistent coloring
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'CREW' || color() === 'TESTS') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'YEAR') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    if (color() === 'EVENT') {
      if (d != undefined && getItemColor) {
        return getItemColor(d);
      }
      return "grey";
    }
    return "grey";
  }

  function getColor(d: MapTrackPoint | null | undefined): string {
    return getOriginalColor(d ?? undefined);
  }

  /**
   * The baseline path(s) to draw: ONE in the mirrored (Normalized) view, where both tacks fold onto
   * the same side and average into a single track, and one per tack in Standard view, where they are
   * mirror-image ground tracks that cannot honestly be merged.
   */
  function getBaselineMapTracks(): Array<{ side: "port" | "stbd" | "all"; points: ManeuverBaselineMapPoint[] }> {
    if (!maneuverBaselineStore.baselineActive()) return [];
    // A load in flight still holds the previous bin's path; drawing it would show a baseline for a
    // wind speed the user has already left.
    if (maneuverBaselineStore.baselineLoading()) return [];
    const data = maneuverBaselineStore.baselineTracks();
    if (!data) return [];
    if (data.path.length > 1) {
      return [{ side: "all", points: data.path }];
    }
    return [
      { side: "port", points: data.pathsBySide.port },
      { side: "stbd", points: data.pathsBySide.stbd },
    ];
  }

  /** Draw (or redraw) the baseline tracks. Removes first, so a zoom re-projects rather than stacking. */
  function drawBaselineTracks(plotG: d3.Selection<SVGGElement, unknown, any, any>): void {
    plotG.selectAll(".maneuver-baseline-track").remove();
    const color = maneuverBaselineStore.baselineColor();
    getBaselineMapTracks().forEach(({ side, points }) => {
      if (!points || points.length < 2) return;
      // Match the maneuver tracks' LatLng convention: (lng, lat) argument order.
      const pathPoints = points.map((p) => ({ ...p, LatLng: new L.LatLng(p.lng, p.lat) })) as MapTrackPoint[];
      const pathString = toMapLinePath(pathPoints);
      if (!pathString || pathString.includes("NaN")) return;
      plotG
        .append("path")
        .attr("class", baselineTrackCssClass(side === "port" ? "port" : undefined))
        .attr("data-baseline-side", side)
        .style("stroke", color)
        .attr("d", pathString);
    });
  }

  /** The tracks are fetched, so they land after DrawMap ran. Redraw just the baseline when they do. */
  createEffect(() => {
    maneuverBaselineStore.baselineActive();
    maneuverBaselineStore.baselineLoading();
    maneuverBaselineStore.baselineTracks();
    maneuverBaselineStore.baselineColor();
    if (!mapInitialized()) return;
    const layer = g;
    if (!layer || !layer.node()) return;
    untrack(() => drawBaselineTracks(layer));
  });

  /** Stroke/fill color for grouped .mapPath and boats — keeps boats aligned with track (MIX, selection, etc.) */
  function groupedTrackStrokeColor(firstPoint: MapTrackPoint | null | undefined): string {
    const sel = selectedGroupKeys().length > 0;
    const mix = groupDisplayMode() === 'MIX';
    const styleSel = sel || mix;
    const pt = firstPoint ?? undefined;
    if (!styleSel) return getColor(pt);
    if (isPathSelected(firstPoint)) return getColor(pt);
    return mix && !sel ? getColor(pt) : "lightgrey";
  }

  function DrawMap(
    maneuvers: MapTrackPoint[][],
    groupedManeuvers: MapTrackPoint[][] | null = null,
    sourceEventIds?: readonly number[]
  ) {
    // Verify map is initialized before drawing
    if (!mapInitialized() || !map) {
      debug('🗺️ Map.jsx: DrawMap - Map is not initialized!');
      return;
    }
    
    const mixMode = groupedManeuvers !== null;
    
    // Verify g element exists and is attached, recreate if missing
    const gNode = g && g.node ? g.node() : null;
    const gInDom = gNode && gNode.parentNode;
    
    if (!g || !gNode || !gInDom) {
      // Try to recreate the g element if it's missing
      try {
        svg = d3.select(map.getPanes().overlayPane).select("svg");
        if (svg.node()) {
          // Remove any existing g elements
          svg.selectAll("g").remove();
          // Recreate the g element
          g = svg.append("g").attr("width", mapWidth).attr("height", mapHeight)
            .attr("z-index", 1000);
          debug('🗺️ Map.jsx: DrawMap - Recreated G element');
        } else {
          logError('🗺️ Map.jsx: DrawMap - SVG element is missing, cannot recreate G element!');
          return;
        }
      } catch (error: any) {
        logError('🗺️ Map.jsx: DrawMap - Failed to recreate G element:', error);
        return;
      }
    }

    if (!g) {
      logError('🗺️ Map.jsx: DrawMap - G element is missing');
      return;
    }
    const plotG = g;

    // Clear all pending boat fade timeouts when starting a new draw
    boatFadeTimeouts.forEach(timeout => clearTimeout(timeout));
    boatFadeTimeouts = [];
    
    d3MapPane().select("svg").selectAll(".line").remove()
    d3MapPane().select("svg").selectAll(".solid_line").remove()
    d3MapPane().select("svg").selectAll(".map_dash_line").remove()
    d3MapPane().select("svg").selectAll(".mapPath").remove()
    d3MapPane().select("svg").selectAll(".boat").interrupt().remove()
    d3MapPane().select("svg").selectAll(".boat-circle").interrupt().remove()
    d3MapPane().select("svg").selectAll(".hover-circle").remove()
    // Clear the MIX-mode individual (ungrouped) circle layer up front. These are keyed by
    // event id below, so stale circles from a previous maneuver type would otherwise never
    // hit an exit() selection and would linger on the map after the type changes.
    d3MapPane().select("svg").selectAll(".individual-circle").remove()

    const click = function (event: MouseEvent, d: MapTrackPoint) {
      // Handle case where d might be undefined or doesn't have event_id
      if (!d) return;
      
      // Try to get event_id from the data point
      event.stopPropagation();
      const groupKey = getManeuverGroupKeyFromFirstPoint(d as Record<string, unknown>, color());
      setSelectedGroupKeys((prev) =>
        prev.some((k) => groupKeyEquals(k, groupKey))
          ? prev.filter((k) => !groupKeyEquals(k, groupKey))
          : [...prev, groupKey]
      );
      setTriggerSelection(true);
    };

    // Mouse event handlers
    // Offset tooltip left of cursor so it stays left on any monitor (e.g. big display)
    const TOOLTIP_LEFT_OFFSET_PX = 500;
    const mouseover = (event: MouseEvent, d: MapTrackPoint) => {
      const tooltipContent = getTooltipContent(d); 

      setTooltip({
        visible: true,
        content: tooltipContent,
        x: event.pageX - TOOLTIP_LEFT_OFFSET_PX,
        y: event.pageY - 100
      });
    };

    const mousemove = (event: MouseEvent, d: MapTrackPoint) => {
      const tooltipContent = getTooltipContent(d); 

      setTooltip({
        visible: true,
        content: tooltipContent,
        x: event.pageX - TOOLTIP_LEFT_OFFSET_PX,
        y: event.pageY - 100
      });
    };

    const mouseout = () => {
      setTooltip({
        visible: false,
        content: "",
        x: 0,
        y: 0
      });
    }; 

    const getTooltipContent = (point: MapTrackPoint | undefined) => {
      if (!point) return "";
  
      // Check if we have State/Config (GP50)
      const hasStateConfig = point.State !== undefined || point.Config !== undefined;
      
      // Use template literals to build an HTML table dynamically
      const eventLabel =
        typeof point.event_id === 'number' && point.event_id < 0 ? 'Group average' : String(point.event_id ?? '');
      let tooltipRows = `
              <tr><td>EVENT ID:</td><td>${eventLabel}</td></tr>`;
      
      // Add source_name if available (for fleet/historical contexts)
      if (point.source_name) {
        tooltipRows += `
              <tr><td>SOURCE:</td><td>${point.source_name}</td></tr>`;
      }
      
      tooltipRows += `
              <tr><td>TWS BIN:</td><td>${point.tws_bin}</td></tr>`;
      
      if (hasStateConfig) {
        // GP50: Show State and Config
        tooltipRows += `
              <tr><td>STATE:</td><td>${point.State ?? ''}</td></tr>
              <tr><td>CONFIG:</td><td>${point.Config ?? ''}</td></tr>
              <tr><td>CREW:</td><td>${point.Crew ?? point.crew ?? point.CREW_CODE ?? ''}</td></tr>
              <tr><td>TESTS:</td><td>${point.Tests ?? point.tests ?? point.TEST ?? 'NONE'}</td></tr>`;
      }
      
      tooltipRows += `
              <tr><td>RACE:</td><td>${point.race ?? ''}</td></tr>`;
      
      // Only show DATE if it exists in the data
      if (point.date !== undefined && point.date !== null && point.date !== '') {
        tooltipRows += `
              <tr><td>DATE:</td><td>${point.date}</td></tr>`;
      }
      
      // TIME should be in the data - use it directly
      if (point.time !== undefined && point.time !== null && String(point.time) !== '') {
        tooltipRows += `
              <tr><td>TIME:</td><td>${point.time}</td></tr>`;
      }
      
      tooltipRows += `
              <tr><td>TWA:</td><td>${point.twa ?? ''}</td></tr>
              <tr><td>VMG_PERCENT:</td><td>${point.vmg_perc_avg ?? ''}</td></tr>`;
      
      return `<table class='table-striped'>${tooltipRows}</table>`;  
    };

    maneuverPathsData = [];
    let boatdataArray: [MapTrackPoint, MapTrackPoint][] = [];

    // MIX MODE: First draw individual tracks as background layer (dimmed, no events)
    if (mixMode && maneuvers != undefined) {
      const individualPaths: ManeuverPathRecord[] = [];

      maneuvers.forEach(function (maneuver: MapTrackPoint[]) {
        if (!maneuver || maneuver.length === 0) return;
        const eventId = maneuver[0].event_id;
        if (eventId === undefined || eventId === null) return;
        
        const validPoints = maneuver.filter(d => {
          if (!d || !d.LatLng) return false;
          if (d.LatLng.lat !== undefined && d.LatLng.lng !== undefined) {
            const lat = d.LatLng.lat;
            const lng = d.LatLng.lng;
            return !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng);
          }
          return false;
        });
        if (validPoints.length < 2) return;
        
        individualPaths.push({
          eventId: eventId,
          points: validPoints,
          firstPoint: validPoints[0]
        });
      });
      
      // Individual (ungrouped) tracks in MIX mode: coloured by group and faint (opacity 0.3), matching the
      // grouped TimeSeries MIX individual tracks so the two views are consistent.
      const individualSelection = plotG
        .selectAll(".mapPath-individual")
        .data(individualPaths, (d: unknown) => (d as ManeuverPathRecord).eventId);

      individualSelection
        .enter()
        .append("path")
        .attr("class", "mapPath-individual")
        .attr("data-event-id", (d: ManeuverPathRecord) => d.eventId)
        .style("stroke", (d: ManeuverPathRecord) => getColor(d.firstPoint))
        .merge(individualSelection as unknown as d3.Selection<SVGPathElement, ManeuverPathRecord, SVGGElement, unknown>)
        .attr("data-event-id", (d: ManeuverPathRecord) => d.eventId)
        .style("stroke", (d: ManeuverPathRecord) => getColor(d.firstPoint))
        .style("stroke-width", 0.5)
        .style("stroke-opacity", 0.3)
        .style("stroke-linecap", "round")
        .style("fill", "none")
        .style("pointer-events", "none")
        .attr("d", (d: ManeuverPathRecord) => {
          if (!map || !d || !d.points) return "";
          const pathString = toMapLinePath(d.points);
          if (pathString && !pathString.includes("NaN")) {
            return pathString;
          }
          return "";
        });
      
      individualSelection.exit().remove();
      
      // Draw small circles for individual tracks (no boats, no events) — every 5 seconds
      individualPaths.forEach(maneuver => {
        const boatPairs = getBoatPositionsByTime(maneuver.points);
        const circleData = boatPairs.map(([, p]) => p);
        
        const circles = plotG.selectAll(`.individual-circle-${maneuver.eventId}`).data(circleData);

        circles
          .enter()
          .append("circle")
          .attr("class", `individual-circle individual-circle-${maneuver.eventId}`)
          .merge(circles as unknown as d3.Selection<SVGCircleElement, MapTrackPoint, SVGGElement, unknown>)
          .attr("cx", (d: MapTrackPoint) => {
            if (!d || !d.LatLng || !map) return 0;
            const point = applyLatLngToLayer(d.LatLng);
            return isNaN(point.x) ? 0 : point.x;
          })
          .attr("cy", (d: MapTrackPoint) => {
            if (!d || !d.LatLng || !map) return 0;
            const point = applyLatLngToLayer(d.LatLng);
            return isNaN(point.y) ? 0 : point.y;
          })
          .attr("r", 1.5)
          .style("fill", getColor(maneuver.firstPoint))
          .style("stroke", "none")
          .style("opacity", 0.3)
          .style("pointer-events", "none");
        
        circles.exit().remove();
      });
    } else {
      // ON mode: remove individual layer so only grouped tracks show
      plotG.selectAll(".mapPath-individual").remove();
      plotG.selectAll(".individual-circle").remove();
    }

    // Now draw grouped tracks (or all tracks in ON mode)
    const tracksToDrawAsGrouped = mixMode ? groupedManeuvers : maneuvers;
    
    if (tracksToDrawAsGrouped != undefined) {
      // Convert maneuvers to single paths per maneuver
      const maneuverPaths: ManeuverPathRecord[] = [];

      tracksToDrawAsGrouped.forEach(function (maneuver: MapTrackPoint[]) {
        if (!maneuver || maneuver.length === 0) return;
        
        const eventId = maneuver[0].event_id;
        if (eventId === undefined || eventId === null) return;
        
        // Filter points to remove null/invalid LatLng and validate coordinates
        const validPoints = maneuver.filter(d => {
          if (!d || !d.LatLng) return false;
          // Validate LatLng is a valid Leaflet LatLng object or has lat/lng properties
          if (d.LatLng.lat !== undefined && d.LatLng.lng !== undefined) {
            const lat = d.LatLng.lat;
            const lng = d.LatLng.lng;
            return !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng);
          }
          return false;
        });
        if (validPoints.length < 2) return; // Need at least 2 points for a line
        
        maneuverPaths.push({
          eventId: eventId,
          points: validPoints,
          firstPoint: validPoints[0]
        });
      });
      
      // Separate selected and non-selected paths
      const selectedPaths: ManeuverPathRecord[] = [];
      const nonSelectedPaths: ManeuverPathRecord[] = [];

      maneuverPaths.forEach((maneuver) => {
        if (isPathSelected(maneuver.firstPoint)) {
          selectedPaths.push(maneuver);
        } else {
          nonSelectedPaths.push(maneuver);
        }
      });

      const orderedPaths = [...nonSelectedPaths, ...selectedPaths];
      const hasSelection = selectedGroupKeys().length > 0;
      const mixMode = groupDisplayMode() === 'MIX';
      // In MIX mode treat all tracks as having "selection" for styling: unselected = dimmed, selected = normal
      const hasSelectionForStyle = hasSelection || mixMode;

      // Bind to paths using eventId as key
      const pathsSelection = plotG
        .selectAll(".mapPath")
        .data(orderedPaths, (d: unknown) => (d as ManeuverPathRecord).eventId);
      
      pathsSelection
        .enter()
        .append("path")
        .attr("class", "mapPath solid_line") // Grouped mode: draw all maneuvers as solid lines
        .attr("data-event-id", d => d.eventId) // Store eventId as attribute for lookup
        .merge(pathsSelection as unknown as d3.Selection<SVGPathElement, ManeuverPathRecord, SVGGElement, unknown>)
        .attr("class", "mapPath solid_line") // Grouped mode: all maneuvers solid
        .attr("data-event-id", (d: ManeuverPathRecord) => d.eventId) // Ensure update selection also has the attribute
        .style("stroke", (d: ManeuverPathRecord) => groupedTrackStrokeColor(d.firstPoint))
        .style("stroke-width", (d: ManeuverPathRecord) => {
          if (mixMode) return 2; // grouped tracks are 2px in MIX mode
          if (!hasSelectionForStyle) return 1;
          const isSelected = isPathSelected(d.firstPoint);
          return isSelected ? 3 : 0.5;
        })
        .style("stroke-opacity", (d: ManeuverPathRecord) => {
          if (!hasSelectionForStyle) return 1;
          // MIX with no selection: full opacity so grouped tracks are visible
          if (mixMode && !hasSelection) return 1;
          const isSelected = isPathSelected(d.firstPoint);
          return isSelected ? 1 : 0.3;
        })
        .style("stroke-linecap", "round")
        .style("fill", "none")
        .style("pointer-events", "none") // Disable pointer events on path, circles will handle it
        .attr("d", (d: ManeuverPathRecord) => {
          if (!map || !d || !d.points) return "";
          const pathString = toMapLinePath(d.points);
          // Validate path string doesn't contain NaN
          if (pathString && !pathString.includes("NaN")) {
            return pathString;
          }
          return "";
        });

      // Reorder paths in DOM so selected paths render on top
      // In SVG, elements that appear later in DOM are rendered on top
      if (hasSelectionForStyle && selectedPaths.length > 0) {
        const parent = plotG.node();
        if (parent) {
          const pathNodes: { node: Element; isSelected: boolean }[] = [];
          pathsSelection.each(function (d: unknown) {
            const rec = d as ManeuverPathRecord;
            const node = this as SVGPathElement;
            const isSelected = isPathSelected(rec.firstPoint);
            pathNodes.push({ node, isSelected });
          });

          const nonSelected = pathNodes.filter((p) => !p.isSelected);
          const selected = pathNodes.filter((p) => p.isSelected);

          [...nonSelected, ...selected].forEach(({ node }) => {
            parent.appendChild(node);
          });
        }
      }
      
      // Store path data for updates
      maneuverPathsData = orderedPaths;

      // Remove any existing outlines (we don't want outlines)
      plotG.selectAll(".mapPath-outline").remove();

      // The reference goes on after the maneuver tracks so it reads on top of them.
      drawBaselineTracks(plotG);

      // Add invisible circles for hover detection and tooltips (MIX: no mouse events on unselected)
      orderedPaths.forEach(maneuver => {
        const isSelected = isPathSelected(maneuver.firstPoint);
        const noPointerEvents = mixMode && !isSelected;
        const hoverCircles = plotG.selectAll(`.hover-circle-${maneuver.eventId}`).data(maneuver.points);

        const circlesMerge = hoverCircles
          .enter()
          .append("circle")
          .attr("class", `hover-circle hover-circle-${maneuver.eventId}`)
          .merge(hoverCircles as unknown as d3.Selection<SVGCircleElement, MapTrackPoint, SVGGElement, unknown>)
          .attr("cx", (d: MapTrackPoint) => {
            if (!d || !d.LatLng || !map) return 0;
            const point = applyLatLngToLayer(d.LatLng);
            return isNaN(point.x) ? 0 : point.x;
          })
          .attr("cy", (d: MapTrackPoint) => {
            if (!d || !d.LatLng || !map) return 0;
            const point = applyLatLngToLayer(d.LatLng);
            return isNaN(point.y) ? 0 : point.y;
          })
          .attr("r", 5) // Hover detection radius
          .style("fill", "transparent")
          .style("stroke", "none")
          .style("pointer-events", noPointerEvents ? "none" : "all")
          .style("cursor", noPointerEvents ? "default" : "pointer");
        if (!noPointerEvents) {
          circlesMerge
            .on("mouseover", mouseover)
            .on("mouseout", mouseout)
            .on("mousemove", mousemove)
            .on("click", click);
        } else {
          circlesMerge.on("mouseover", null).on("mouseout", null).on("mousemove", null).on("click", null);
        }
        
        hoverCircles.exit().remove();
      });
      
      // Handle boats — draw every 5 seconds along each track
      orderedPaths.forEach(maneuver => {
        const boatPairs = getBoatPositionsByTime(maneuver.points);
        boatdataArray.push(...boatPairs);
      });

      // Boats will be drawn in doUpdates function
      // (const arrow rather than a block-scoped function declaration; only called later at zoomend/below.)
      const doUpdates = () => {
        if (!map) return;
        zoom_level = map.getZoom();

        // Verify g still exists and is in DOM before drawing
        const currentGNode = g && g.node ? g.node() : null;

        if (!g || !currentGNode) {
          logError('🗺️ Map.jsx: doUpdates - G element is missing!');
          return;
        }

        const hasSelection = selectedGroupKeys().length > 0;
        const mixModeDoUpdates = groupDisplayMode() === 'MIX';
        const hasSelectionForStyle = hasSelection || mixModeDoUpdates;
        
        // Update all paths - select directly from DOM to ensure we get all paths
        if (!map || !g) return; // Ensure map is ready
        const layer = g!;

        const pathNodes: { node: Element; isSelected: boolean }[] = [];

        layer.selectAll(".mapPath").each(function (d: unknown) {
          const node = this as SVGPathElement;

          let maneuver: ManeuverPathRecord | undefined = d as ManeuverPathRecord | undefined;
          if (!maneuver || !maneuver.points) {
            const eventIdAttr = d3.select(node).attr("data-event-id");
            if (eventIdAttr && maneuverPathsData.length > 0) {
              maneuver = maneuverPathsData.find((m) => String(m.eventId) === eventIdAttr);
            }
          }
          
          if (!maneuver || !maneuver.points) return;
          
          const firstPoint = maneuver.firstPoint;
          const isSelected = hasSelectionForStyle && isPathSelected(firstPoint);
          
          if (!firstPoint) return;
          
          const pathString = toMapLinePath(maneuver.points);
          if (pathString && pathString !== 'M0,0' && !pathString.includes("NaN")) {
            d3.select(node).attr("d", pathString);
          }
          
          node.style.setProperty("stroke", groupedTrackStrokeColor(firstPoint), "important");
          const unselectedWidth = hasSelectionForStyle && !(mixModeDoUpdates && !hasSelection) ? "0.5" : "1";
          const unselectedOpacity = hasSelectionForStyle && !(mixModeDoUpdates && !hasSelection) ? "0.2" : "1";
          node.style.setProperty("stroke-width", mixModeDoUpdates ? "2" : (isSelected ? "3" : unselectedWidth), "important");
          node.style.setProperty("stroke-opacity", isSelected ? "1" : unselectedOpacity, "important");
          
          pathNodes.push({ node, isSelected });
        });
        
        // Reorder paths in DOM so selected paths render on top
        // In SVG, elements that appear later in DOM are rendered on top
        if (hasSelectionForStyle && pathNodes.length > 0) {
          const parent = layer.node();
          if (parent) {
            // Separate selected and non-selected
            const nonSelected = pathNodes.filter(p => !p.isSelected);
            const selected = pathNodes.filter(p => p.isSelected);
            
            // Reorder: non-selected first, then selected
            [...nonSelected, ...selected].forEach(({ node }) => {
              parent.appendChild(node);
            });
          }
        }

        // Re-project the baseline too: it carries no bound data, so it is redrawn rather than
        // re-attributed like the maneuver paths above.
        drawBaselineTracks(layer);

        //DRAW BOATS
        // Always remove all boats and circles first to ensure clean state (especially when phase changes)
        layer.selectAll(".boat").interrupt().remove();
        layer.selectAll(".boat-circle").interrupt().remove();
        
        boatdataArray.forEach(function (boatData: [MapTrackPoint, MapTrackPoint]) {
          if (!boatData || boatData.length < 2) return;

          let d = boatData[1];
          if (!d || !d.LatLng || !map) return;
          
          // Validate coordinates before drawing boat
          const testPoint = applyLatLngToLayer(d.LatLng);
          if (isNaN(testPoint.x) || isNaN(testPoint.y)) return;
          
          const maneuverForBoat = maneuverPathsData.find(m => m.eventId == d.event_id);
          const trackFirstPoint = maneuverForBoat?.firstPoint ?? d;
          const trackColor = groupedTrackStrokeColor(trackFirstPoint);
          const isBoatSelected = hasSelection && isPathSelected(trackFirstPoint);
          // Grouped tracks layer always shows boat icons (same as ON mode); selection only affects opacity and pointer-events
          const noBoatEvents = false;
          
          let x = applyLatLngToLayer(d.LatLng).x
          let y = applyLatLngToLayer(d.LatLng).y
          
          // Create a unique ID for this boat based on event_id and time
          // Sanitize the time value to ensure it's valid for CSS selectors (handles negative numbers, dots, etc.)
          const sanitizedTime = sanitizeTimeForId(d.time);
          const sanitizedEventId = String(d.event_id || '0').replace(/[^a-zA-Z0-9_-]/g, '_');
          let boatId = `boat-${sanitizedEventId}-${sanitizedTime}`;
          
          // Final safety check: ensure boatId is a valid CSS identifier
          // Remove any remaining invalid characters, consecutive hyphens, and ensure it starts with a letter
          boatId = boatId
            .replace(/[^a-zA-Z0-9_-]/g, '_') // Remove any remaining invalid chars
            .replace(/-+/g, '-') // Replace consecutive hyphens with single hyphen
            .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
          
          // Ensure it starts with a letter (CSS identifier requirement)
          if (/^[0-9_-]/.test(boatId)) {
            boatId = 'b' + boatId;
          }
          
          // Check if boat already exists
          const existingBoat = layer.select(`#${boatId}`);

          // MIX unselected: draw only a circle (no boat icon), no mouse events
          if (noBoatEvents) {
            const circleId = `boat-circle-${sanitizedEventId}-${sanitizedTime}`;
            const existingCircle = layer.select(`#${circleId}`);
            if (existingCircle.empty()) {
              layer.append("circle")
                .datum(d)
                .attr("id", circleId)
                .attr("class", "boat-circle boat-circle-unselected")
                .attr("cx", x)
                .attr("cy", y)
                .attr("r", 2) // Slightly larger for visibility along path
                .style("fill", trackColor)
                .style("stroke", "none")
                .style("opacity", 0.2)
                .style("pointer-events", "none");
            } else {
              existingCircle.attr("cx", x).attr("cy", y).style("fill", trackColor).style("opacity", 0.2);
            }
            return;
          }
          
          let boat: d3.Selection<SVGPathElement, MapTrackPoint, SVGGElement, unknown>;
          if (existingBoat.empty()) {
            // Get current phase to store on boat element
            const currentPhase = phase();

            // Create new boat (grouped selected tracks only when not MIX unselected)
            boat = layer.append("path") as d3.Selection<SVGPathElement, MapTrackPoint, SVGGElement, unknown>;
            boat
              .datum(d) // Bind the point data so event handlers can access it
              .attr("id", boatId)
              .attr("class", "boat")
              .attr("data-phase", currentPhase) // Store phase to verify it hasn't changed
              .attr("data-fade-started", "false") // Flag to track if fade has started
              .style("stroke", "black")
              .style("fill", trackColor)
              .style("opacity", hasSelection ? (isBoatSelected ? 1 : 0.2) : 1)
              .style("pointer-events", "visiblePainted")
              .on("mouseover", mouseover).on("mouseout", mouseout).on("mousemove", mousemove).on("click", click);
            
            let hdg = Number(d.hdg ?? 0) - 180;

            // Get boat path based on icon type and zoom level
            const boatPath = getBoatPathForZoom(zoom_level);
            boat.attr("d", boatPath);
            boat.attr("transform", "translate(" + x + "," + y + ") rotate(" + hdg + ")");
            
            // Create circle that will replace the boat (initially hidden)
            const circleId = `boat-circle-${sanitizedEventId}-${sanitizedTime}`;
            layer.append("circle")
              .datum(d)
              .attr("id", circleId)
              .attr("class", "boat-circle")
              .attr("data-phase", currentPhase)
              .attr("cx", x)
              .attr("cy", y)
              .attr("r", 1.5)
              .style("fill", trackColor)
              .style("stroke", "none")
              .style("opacity", 0)
              .style("pointer-events", "visiblePainted")
              .on("mouseover", mouseover).on("mouseout", mouseout).on("mousemove", mousemove).on("click", click);

            // Start fade transition after delay
            const timeoutId = setTimeout(() => {
              const boatNode = boat.node() as SVGPathElement | null;
              if (!boatNode || !boatNode.parentNode) return;

              // Check if phase has changed - if so, don't start fade
              const boatPhase = boat.attr("data-phase");
              const currentPhaseNow = phase();
              if (boatPhase !== currentPhaseNow) {
                // Phase changed, remove boat and circle
                d3.select(boatNode).remove();
                const circleNode = layer.select(`#${circleId}`).node();
                if (circleNode) d3.select(circleNode).remove();
                return;
              }
              
              // Check if fade has already started
              const fadeStarted = boat.attr("data-fade-started") === "true";
              if (fadeStarted) return;
              
              // Mark fade as started
              boat.attr("data-fade-started", "true");
              
              boat
                .interrupt() // Interrupt any existing transition
                .transition()
                .duration(1000) // Fade out over 1 second
                .style("opacity", 0)
                .on("end", function () {
                  // Check phase again before completing fade
                  const boatPhaseEnd = d3.select(this as SVGPathElement).attr("data-phase");
                  const currentPhaseEnd = phase();
                  if (boatPhaseEnd !== currentPhaseEnd) {
                    // Phase changed during fade, just remove
                    d3.select(this as SVGPathElement).remove();
                    const circleNode = layer.select(`#${circleId}`).node();
                    if (circleNode) d3.select(circleNode).remove();
                    return;
                  }

                  // Remove boat after fade completes
                  const boatNode = this as SVGPathElement;
                  if (boatNode && boatNode.parentNode) {
                    d3.select(boatNode).remove();
                  }

                  // Fade in circle
                  const circleNode = layer.select(`#${circleId}`).node() as SVGCircleElement | null;
                  if (circleNode && circleNode.parentNode) {
                    // Check phase one more time
                    const circlePhase = d3.select(circleNode).attr("data-phase");
                    const currentPhaseCircle = phase();
                    if (circlePhase !== currentPhaseCircle) {
                      d3.select(circleNode).remove();
                      return;
                    }
                    
                    d3.select(circleNode)
                      .interrupt() // Interrupt any existing transition
                      .transition()
                      .duration(500) // Fade in over 0.5 seconds
                      .style("opacity", hasSelection ? (isBoatSelected ? 1 : 0.2) : 1);
                  }
                });
            }, 2000); // Wait 2 seconds before starting fade
            
            // Track the timeout so we can clear it on phase change
            boatFadeTimeouts.push(timeoutId);
          } else {
            // Boat already exists, just update its position, transform, and color
            existingBoat.each(function () {
              const existingBoatSel = d3.select(this as SVGPathElement);
              let hdg = Number(d.hdg ?? 0) - 180;
              const boatPath = getBoatPathForZoom(zoom_level);
              existingBoatSel.attr("d", boatPath);
              existingBoatSel.attr("transform", "translate(" + x + "," + y + ") rotate(" + hdg + ")");
              existingBoatSel.style("fill", trackColor);
            });
            
            // Update or create circle for existing boat
            // Use the same sanitization as boatId for consistency
            const circleSanitizedTime = sanitizeTimeForId(d.time);
            const circleSanitizedEventId = String(d.event_id || '0').replace(/[^a-zA-Z0-9_-]/g, '_');
            let circleId = `boat-circle-${circleSanitizedEventId}-${circleSanitizedTime}`;
            
            // Final safety check: ensure circleId is a valid CSS identifier
            circleId = circleId
              .replace(/[^a-zA-Z0-9_-]/g, '_')
              .replace(/-+/g, '-')
              .replace(/^-+|-+$/g, '');
            
            if (/^[0-9_-]/.test(circleId)) {
              circleId = 'b' + circleId;
            }
            
            const existingCircle = layer.select(`#${circleId}`);

            if (existingCircle.empty()) {
              layer.append("circle")
                .datum(d)
                .attr("id", circleId)
                .attr("class", "boat-circle")
                .attr("cx", x)
                .attr("cy", y)
                .attr("r", 1.5)
                .style("fill", trackColor)
                .style("stroke", "none")
                .style("opacity", 0)
                .style("pointer-events", "visiblePainted")
                .on("mouseover", mouseover)
                .on("mouseout", mouseout)
                .on("mousemove", mousemove)
                .on("click", click);
            } else {
              existingCircle
                .attr("cx", x)
                .attr("cy", y)
                .style("fill", trackColor);
            }
          }
        });

        // MIX mode: move unselected circles behind all paths so grouped (selected) tracks draw on top
        if (mixModeDoUpdates) {
          const firstPath = layer.select(".mapPath").node() as SVGPathElement | null;
          const unselectedCircleNodes = layer.selectAll(".boat-circle-unselected").nodes() as Element[];
          const gParent = layer.node();
          if (firstPath && gParent && unselectedCircleNodes.length > 0) {
            unselectedCircleNodes.reverse().forEach((node) => {
              gParent.insertBefore(node, firstPath);
            });
          }
        }

        // Update hover circle positions on zoom/pan
        layer.selectAll(".hover-circle").each(function (d: unknown) {
          const pt = d as MapTrackPoint | undefined;
          if (pt && pt.LatLng && map) {
            const point = applyLatLngToLayer(pt.LatLng);
            if (!isNaN(point.x) && !isNaN(point.y)) {
              d3.select(this as SVGCircleElement)
                .attr("cx", point.x)
                .attr("cy", point.y);
            }
          }
        });

        // Update boat circle positions on zoom/pan
        layer.selectAll(".boat-circle").each(function (d: unknown) {
          const pt = d as MapTrackPoint | undefined;
          if (pt && pt.LatLng && map) {
            const point = applyLatLngToLayer(pt.LatLng);
            if (!isNaN(point.x) && !isNaN(point.y)) {
              d3.select(this as SVGCircleElement)
                .attr("cx", point.x)
                .attr("cy", point.y);
            }
          }
        });

        // Update individual (grey) track path geometry on zoom/pan (MIX mode).
        // Without this, the grouped paths reposition but the grey background tracks stay
        // pinned to their previous pixel coordinates, so they drift out of the map origin.
        layer.selectAll(".mapPath-individual").each(function (d: unknown) {
          const rec = d as ManeuverPathRecord | undefined;
          if (!rec || !rec.points || !map) return;
          const pathString = toMapLinePath(rec.points);
          if (pathString && !pathString.includes("NaN")) {
            d3.select(this as SVGPathElement).attr("d", pathString);
          }
        });

        // Update individual circle positions on zoom/pan (MIX mode)
        layer.selectAll(".individual-circle").each(function (d: unknown) {
          const pt = d as MapTrackPoint | undefined;
          if (pt && pt.LatLng && map) {
            const point = applyLatLngToLayer(pt.LatLng);
            if (!isNaN(point.x) && !isNaN(point.y)) {
              d3.select(this as SVGCircleElement)
                .attr("cx", point.x)
                .attr("cy", point.y);
            }
          }
        });
      }

      map.on("zoomend", doUpdates);
      doUpdates();
      
      // Fit after the first draw, a phase change, or a maneuver-type change — but not the
      // draw that still contains the previous type's events (see scheduleRecenter).
      // Grouped tracks use synthetic ids, so the fit decision uses the source event ids.
      const visibleTracks = groupedManeuvers ? [...maneuvers, ...groupedManeuvers] : maneuvers;
      scheduleRecenter(visibleTracks, sourceEventIds);
      } 

    if (selectedGroupKeys().length > 0) {
      updateSelection();
    }
  }

  function drawFeatures() {
    if (!map || !svg || !g) {
      return;
    }
    
    function doUpdates() {
      if (!map || !svg || !g) return;
      
      // Check if map is fully loaded before accessing bounds
      if (!map._loaded || !map._container) return;
      
      svg.selectAll("line.crosstrack").remove()

      let zoom_level;
      try {
        zoom_level = map.getZoom();
      } catch (e) {
        // Map not ready yet
        return;
      }

      if (zoom_level > 2) {
        // Get current map bounds with a buffer
        let bounds;
        try {
          bounds = map.getBounds();
          // Check if bounds is valid by checking if it has required methods
          if (!bounds || typeof bounds.getSouth !== 'function' || 
              typeof bounds.getNorth !== 'function' || 
              typeof bounds.getWest !== 'function' || 
              typeof bounds.getEast !== 'function') {
            return;
          }
        } catch (e) {
          // Map bounds not available yet
          return;
        }
        
        const buffer = 0.01; // ~1km buffer in degrees
        const minLat = bounds.getSouth() - buffer;
        const maxLat = bounds.getNorth() + buffer;
        const minLon = bounds.getWest() - buffer;
        const maxLon = bounds.getEast() + buffer;
        
        const baseLat = 39.476984;
        const baseLon = -0.291140;
        // const latint = 0.000224956; //25m increments
        const latint = 0.000899872; //100m increments

        // Calculate start and end indices based on visible bounds
        const startIndexNorth = Math.max(0, Math.floor((minLat - baseLat) / latint));
        const endIndexNorth = Math.ceil((maxLat - baseLat) / latint);
        
        const startIndexSouth = Math.max(0, Math.floor((baseLat - maxLat) / latint));
        const endIndexSouth = Math.ceil((baseLat - minLat) / latint);

        // Draw northward lines (only those in visible range)
        for (let i = startIndexNorth; i <= endIndexNorth && i < 1000; i++) {
          if (i < 0) continue;
          
          let lat = baseLat + (i * latint);
          
          // Only draw if line is within visible bounds
          if (lat >= minLat && lat <= maxLat) {
            let fromLatLng = new L.LatLng(lat, baseLon - 0.2)
            let toLatLng = new L.LatLng(lat, baseLon + 0.2)
            
            // Check if line endpoints are within visible longitude range
            if ((baseLon - 0.2 <= maxLon && baseLon - 0.2 >= minLon) || 
                (baseLon + 0.2 <= maxLon && baseLon + 0.2 >= minLon) ||
                (baseLon - 0.2 <= minLon && baseLon + 0.2 >= maxLon)) {
              
              g.append('line')
                .attr("class", "crosstrack")
                .style("stroke", "lightgray")
                .style("stroke-width", 1)
                .attr("x1", applyLatLngToLayer(fromLatLng).x)
                .attr("y1", applyLatLngToLayer(fromLatLng).y)
                .attr("x2", applyLatLngToLayer(toLatLng).x)
                .attr("y2", applyLatLngToLayer(toLatLng).y)
            }
          }
        }

        // Draw southward lines (only those in visible range)
        for (let i = startIndexSouth; i <= endIndexSouth && i < 1000; i++) {
          if (i < 0) continue;
          
          let lat = baseLat - (i * latint);
          
          // Only draw if line is within visible bounds
          if (lat >= minLat && lat <= maxLat) {
            let fromLatLng = new L.LatLng(lat, baseLon - 0.2)
            let toLatLng = new L.LatLng(lat, baseLon + 0.2)
            
            // Check if line endpoints are within visible longitude range
            if ((baseLon - 0.2 <= maxLon && baseLon - 0.2 >= minLon) || 
                (baseLon + 0.2 <= maxLon && baseLon + 0.2 >= minLon) ||
                (baseLon - 0.2 <= minLon && baseLon + 0.2 >= maxLon)) {
              
              g.append('line')
                .attr("class", "crosstrack")
                .style("stroke", "lightgray")
                .style("stroke-width", 1)
                .attr("x1", applyLatLngToLayer(fromLatLng).x)
                .attr("y1", applyLatLngToLayer(fromLatLng).y)
                .attr("x2", applyLatLngToLayer(toLatLng).x)
                .attr("y2", applyLatLngToLayer(toLatLng).y)
            }
          }
        }
      }
    } 

    if (map) {
      // Wait for map to be fully loaded before attaching listeners
      const setupListeners = () => {
        // Check if map is ready
        if (!map || !map._loaded || !map._container) {
          return;
        }
        
        // Map is loaded, attach event listeners (only once)
        // Check if listeners are already attached to avoid duplicates
        if (!map._crosstrackListenersAttached) {
          map.on("zoomend", doUpdates);
          map.on("load", doUpdates); // Also update when map loads/reloads
          map._crosstrackListenersAttached = true;
        }
        
        // Call doUpdates once map is ready
        doUpdates();
      };
      
      // Start setup - will wait for load if needed
      if (map._loaded && map._container) {
        // Map already loaded, setup immediately
        setupListeners();
      } else {
        // Wait for map to load
        map.once("load", setupListeners);
        // Also try after a short delay in case load event already fired
        setTimeout(() => {
          if (map && map._loaded && map._container && !map._crosstrackListenersAttached) {
            setupListeners();
          }
        }, 100);
      }
    }
  }

  function updateSelection() {
    // Use untrack to prevent updateMinMaxRanges from creating reactive dependencies
    untrack(() => {
      updateMinMaxRanges();
    });

    if (!g || !g.node()) {
      return;
    }
    const ug = g;

    const hasSelection = selectedGroupKeys().length > 0;
    const mixModeUpdate = groupDisplayMode() === 'MIX';
    const pathNodes: { node: Element; isSelected: boolean }[] = [];
    
    ug.selectAll(".mapPath").each(function (d: unknown) {
      const maneuver = d as ManeuverPathRecord | undefined;
      if (!maneuver) return;
      const node = this as SVGPathElement;
      const firstPoint = maneuver.firstPoint;
      const isSelected = hasSelection && isPathSelected(firstPoint);
      if (!firstPoint) return;
      
      node.style.setProperty("stroke", groupedTrackStrokeColor(firstPoint), "important");
      node.style.setProperty("stroke-width", mixModeUpdate ? "2" : (isSelected ? "3" : (hasSelection ? "0.5" : "1")), "important");
      node.style.setProperty("stroke-opacity", isSelected ? "1" : (hasSelection ? "0.2" : "1"), "important");
      pathNodes.push({ node, isSelected });
    });
    
    if (hasSelection && pathNodes.length > 0) {
      const parent = ug.node();
      if (parent) {
        const nonSelected = pathNodes.filter(p => !p.isSelected);
        const selected = pathNodes.filter(p => p.isSelected);
        [...nonSelected, ...selected].forEach(({ node }) => {
          parent.appendChild(node);
        });
      }
    }

    // MIX mode: individual layer coloured by group (opacity stays 0.3 from the draw), consistent with the
    // grouped TimeSeries individual tracks. This pass runs after the draw with !important, so it previously
    // forced them back to lightgrey — colour by group here too.
    if (mixModeUpdate) {
      ug.selectAll(".mapPath-individual").each(function (d: unknown) {
        const rec = d as ManeuverPathRecord | undefined;
        (this as SVGPathElement).style.setProperty("stroke", getColor(rec?.firstPoint), "important");
      });
      ug.selectAll(".individual-circle").each(function (d: unknown) {
        (this as SVGCircleElement).style.setProperty("fill", getColor(d as MapTrackPoint), "important");
      });
    }

    ug.selectAll(".boat").each(function (d: unknown) {
      const pt = d as MapTrackPoint | undefined;
      if (!pt) return;
      const node = this as SVGPathElement;
      const fp = maneuverPathsData.find((m) => m.eventId === pt.event_id)?.firstPoint ?? pt;
      const boatGroupKey = getManeuverGroupKeyFromFirstPoint(pt as Record<string, unknown>, color());
      const isBoatSelected = hasSelection && selectedGroupKeys().some((k) => groupKeyEquals(k, boatGroupKey));
      const noBoatEvents = groupDisplayMode() === 'MIX' && !isBoatSelected;
      d3.select(node)
        .style("fill", groupedTrackStrokeColor(fp))
        .style("opacity", hasSelection ? (isBoatSelected ? 1 : 0.2) : 1)
        .style("pointer-events", noBoatEvents ? "none" : "visiblePainted");
      if (noBoatEvents) {
        d3.select(node).on("mouseover", null).on("mouseout", null).on("mousemove", null).on("click", null);
      }
    });

    ug.selectAll(".boat-circle").each(function (d: unknown) {
      const pt = d as MapTrackPoint | undefined;
      if (!pt) return;
      const node = this as SVGCircleElement;
      const fp = maneuverPathsData.find((m) => m.eventId === pt.event_id)?.firstPoint ?? pt;
      const boatGroupKey = getManeuverGroupKeyFromFirstPoint(pt as Record<string, unknown>, color());
      const isBoatSelected = hasSelection && selectedGroupKeys().some((k) => groupKeyEquals(k, boatGroupKey));
      const noBoatEvents = groupDisplayMode() === 'MIX' && !isBoatSelected;
      d3.select(node)
        .style("fill", groupedTrackStrokeColor(fp))
        .style("opacity", hasSelection ? (isBoatSelected ? 1 : 0.2) : 1)
        .style("pointer-events", noBoatEvents ? "none" : "visiblePainted");
      if (noBoatEvents) {
        d3.select(node).on("mouseover", null).on("mouseout", null).on("mousemove", null).on("click", null);
      }
    });

    // MIX mode: update hover circles pointer-events and remove handlers when non-interactive
    const mixMode = groupDisplayMode() === 'MIX';
    if (maneuverPathsData && maneuverPathsData.length > 0) {
      maneuverPathsData.forEach((maneuver: ManeuverPathRecord) => {
        const isSelected = isPathSelected(maneuver.firstPoint);
        const noPointerEvents = mixMode && !isSelected;
        const sel = ug.selectAll(`.hover-circle-${maneuver.eventId}`);
        sel.style("pointer-events", noPointerEvents ? "none" : "all").style("cursor", noPointerEvents ? "default" : "pointer");
        if (noPointerEvents) {
          sel.on("mouseover", null).on("mouseout", null).on("mousemove", null).on("click", null);
        }
      });
    }

    setTriggerSelection(false);
  }

  let containerRef: HTMLDivElement | undefined;
  let resizeObserver: ResizeObserver | undefined;

  function redrawFromCachedMapdata() {
    if (!map || updateInProgress || mapdata().length === 0) return;
    const data = mapdata();
    const sourceEventIds = positiveEventIdsFromTracks(data);
    const grpMode = groupDisplayMode();
    const mixMode = grpMode === 'MIX';
    let allTracks: MapTrackPoint[][] = data || [];
    let groupedTracks: MapTrackPoint[][] | null = null;

    if (grpMode !== 'OFF' && data && data.length > 0) {
      const averaged = groupAndAverageMapTracks(data, color());
      if (averaged.length > 0) {
        if (mixMode) {
          groupedTracks = averaged;
        } else {
          allTracks = averaged;
        }
      }
    }

    DrawMap(allTracks, groupedTracks, sourceEventIds);
    updateSelection();
  }

  function resizeChart() {
    if (!map) return;
    reinitMap();
    map.invalidateSize();
    drawFeatures();
    untrack(() => redrawFromCachedMapdata());
  }

  const updateMap = () => {
    log(`[Map] updateMap called - updateInProgress: ${updateInProgress}, mapInitialized: ${mapInitialized()}, map exists: ${!!map}`);
    
    // Guard: prevent concurrent updates
    if (updateInProgress) {
      log('[Map] updateMap - Skipping because updateInProgress is true');
      return;
    }
    
    // Guard: ensure map is initialized before updating
    if (!mapInitialized() || !map) {
      log('Map: updateMap called but map is not initialized yet');
      return;
    }
    
    // Guard: don't update if filtered data is not available yet
    const currentFiltered = filtered();
    log(`[Map] updateMap - filtered() has ${currentFiltered?.length || 0} event_ids`);
    if (!currentFiltered || !Array.isArray(currentFiltered) || currentFiltered.length === 0) {
      log('Map: updateMap called but filtered data is not available yet');
      return;
    }
    
    // Check if eventType or phase has changed - if so, center map on next draw and clear boats.
    // A type change also records the current ids (see the event-type effect) so this draw is not
    // fitted when it still shows the previous type.
    const currentEventType = eventType();
    const currentPhase = phase();
    if (lastEventType !== null && (lastEventType !== currentEventType || lastPhase !== currentPhase)) {
      invalidatePendingRecenter();
      shouldCenterOnNextDraw = true;
      
      // Clear all pending boat fade timeouts
      boatFadeTimeouts.forEach(timeout => clearTimeout(timeout));
      boatFadeTimeouts = [];
      
      // Completely remove all boats and boat circles when phase/eventType changes
      // Use direct SVG selection to ensure clearing works even if g is not available
      d3MapPane().select("svg").selectAll(".boat").interrupt().remove();
      d3MapPane().select("svg").selectAll(".boat-circle").interrupt().remove();
      // Also clear via g if available
      if (g && g.node()) {
        g.selectAll(".boat").interrupt().remove();
        g.selectAll(".boat-circle").interrupt().remove();
      }
    }
    lastEventType = currentEventType;
    lastPhase = currentPhase;
    
    updateInProgress = true;
    setLoading(true);
    fetchData().then((result) => {
      if (result == null || !map) return;
      const data = Array.isArray(result) ? result : result.dataArray;
      const sourceEventIds = positiveEventIdsFromTracks(data);
      const grpMode = groupDisplayMode();
      const mixMode = grpMode === 'MIX';
      let allTracks: MapTrackPoint[][] = data || [];
      let groupedTracks: MapTrackPoint[][] | null = null;

      if (grpMode !== 'OFF' && data && data.length > 0) {
        const averaged = groupAndAverageMapTracks(data, color());
        if (averaged.length > 0) {
          if (mixMode) {
            groupedTracks = averaged;
          } else {
            allTracks = averaged;
          }
        }
      }

      DrawMap(allTracks, groupedTracks, sourceEventIds);
      updateSelection();
      setFirstLoad(false);
      updateInProgress = false;
      if (props?.instanceId) {
        setLoading(false);
      } else {
        setTimeout(() => {
          if (!map) return;
          setLoading(false);
        }, 500);
      }
      if (triggerUpdate()) {
        setTriggerUpdate(false);
        const fc = filtered().length;
        lastFilteredCount = fc;
        if (fc > 0) {
          filteredWatcherTriggered = true;
        }
        queueMicrotask(() => {
          if (untrack(() => updateInProgress)) return;
          untrack(() => updateMap());
        });
      }
    }).catch((error) => {
      if (map) {
        logError('🗺️ Map.jsx: Error in updateMap fetchData', error);
        setLoading(false);
      }
      updateInProgress = false;
      if (triggerUpdate()) {
        setTriggerUpdate(false);
        const fc = filtered().length;
        lastFilteredCount = fc;
        if (fc > 0) {
          filteredWatcherTriggered = true;
        }
        queueMicrotask(() => {
          if (untrack(() => updateInProgress)) return;
          untrack(() => updateMap());
        });
      }
    });
  };

  // Use createEffect to ensure DOM is ready before initializing
  createEffect(() => {
    // Only initialize once
    if (mapInitialized()) return;
    
    const maneuverMapElement = document.getElementById(maneuverMapElementId());
    
    if (maneuverMapElement && !map) {
      // DOM is ready, initialize map
      initMap();
      
      // Only call drawFeatures and updateMap if map was successfully initialized
      if (map) {
        setMapInitialized(true);
        // Small delay to ensure Leaflet has fully initialized
        setTimeout(() => {
          drawFeatures();
        }, 100);
      }
    }
  });

  // Event types to target for a tag write - a single selected type, else all maneuver types.
  const maneuverEventTypes = (): string[] => {
    const currentEventType = eventType();
    return currentEventType ? [currentEventType] : MANEUVER_EVENT_TYPES;
  };

  // Shared post-write handling for GRADE and STATE updates.
  const afterTagUpdate = () => {
    // Clear selected events after a successful tag update.
    // Group keys are this view's selection, so clear them too - leaving them set would keep the
    // selection banner up (its Show gate includes group keys) with hasSelection already false.
    setSelectedEvents([]);
    setSelectedGroupKeys([]);
    setHasSelection(false);
    setSelection([]);
    setTriggerSelection(true);

    // Trigger parent component to refetch data
    if (props?.onDataUpdate && typeof props.onDataUpdate === 'function') {
      props.onDataUpdate();
    } else {
      // Fallback: trigger update
      setTriggerUpdate(true);
    }
  };

  // Function to perform the GRADE update
  const performGradeUpdate = async (gradeValue: string, selected: number[]) => {
    // Get required values from persistent store
    const { selectedClassName, selectedProjectId } = persistantStore;
    const className = selectedClassName();
    const projectId = selectedProjectId();

    if (!className || !projectId) {
      logError('Cannot update GRADE: missing class_name or project_id');
      return;
    }

    const eventTypes = maneuverEventTypes();

    try {
      // Call admin API to update GRADE for selected events
      const response = await putData(`${apiEndpoints.admin.events}/tags`, {
        class_name: className,
        project_id: projectId,
        events: selected,
        event_types: eventTypes,
        key: 'GRADE',
        value: gradeValue
      });
      
      if (response.success) {
        debug(`Successfully updated GRADE to ${gradeValue} for ${selected.length} event(s)`);
        
        // Grade update in HuniDB will be implemented when available
        // Currently grade updates are handled on next data fetch
        
        // Update grade filter to include the newly graded value
        const currentGrades = selectedGradesManeuvers();
        const gradeValueStr = String(gradeValue);
        if (!currentGrades.includes(gradeValueStr)) {
          setSelectedGradesManeuvers([...currentGrades, gradeValueStr]);
          debug(`Added grade ${gradeValue} to selectedGrades filter`);
        }

        afterTagUpdate();
      } else {
        logError('Failed to update GRADE:', response.message || 'Unknown error');
      }
    } catch (error: any) {
      logError('Error updating GRADE:', error);
    }
  };

  onMount(() => {
    requestAnimationFrame(() => {
      if (mapInitialized() || map) return;
      const el = document.getElementById(maneuverMapElementId());
      if (!el) return;
      initMap();
      if (!map) return;
      setMapInitialized(true);
      drawFeatures();
    });

    resizeObserver = new ResizeObserver((_entries) => {
      resizeChart();
    });

    if (containerRef) {
      resizeObserver.observe(containerRef);
    }

    window.addEventListener('resize', resizeChart);
    
    // Add keyboard listener for GRADE updates (0-5 keys)
    const handleKeyPress = (event: KeyboardEvent) => {
      try {
        // Don't trigger if user is typing in an input field or textarea
        const target = event.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
          return;
        }
        
        // Only handle if there are selected events
        const selected = selectedEvents();
        if (!selected || selected.length === 0) {
          return;
        }
        
        const key = event.key;
        if (shouldBlockGradeLikeHotkeysForPerformanceSelections(key)) {
          return;
        }

        if (!['0', '1', '2', '3', '4', '5'].includes(key)) {
          return;
        }

        // Check if user is NOT a reader (readers cannot grade)
        const currentUser = user();
        if (!currentUser) {
          return;
        }
        
        // Superusers can always grade
        if (currentUser.is_super_user) {
          // Allow grading
        } else {
          // Check if user is a reader - readers cannot grade
          const userPermissions = currentUser.permissions;
          let isReader = false;
          
          if (typeof userPermissions === 'string') {
            isReader = userPermissions === 'reader';
          } else if (typeof userPermissions === 'object' && userPermissions !== null) {
            const permissionValues = Object.values(userPermissions);
            // User is a reader if ALL their permissions are 'reader'
            isReader = permissionValues.length > 0 && permissionValues.every(p => p === 'reader');
          }
          
          if (isReader) {
            return; // Readers cannot grade
          }
        }
        
        // Prevent default behavior and stop propagation
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Show confirmation dialog before proceeding
        const message = `Are you sure you want to update GRADE to ${parseInt(key, 10)} for ${selected.length} selected event(s)?\n\nThis action will modify the data and cannot be easily undone.`;

        // Use window.confirm - it automatically closes when user clicks OK or Cancel
        const confirmed = window.confirm(message);
        if (!confirmed) {
          // User cancelled - dialog is already closed
          return;
        }

        // User confirmed - proceed with the update
        // Call asynchronously to avoid blocking the keydown handler
        setTimeout(() => {
          const update = performGradeUpdate(String(parseInt(key, 10)), selected);
          update.catch((error: unknown) => {
            logError('Error in maneuver hotkey update:', error);
          });
        }, 0);
      } catch (error: any) {
        logError('Error in handleKeyPress:', error);
        event.preventDefault();
        event.stopPropagation();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);

    // onCleanup, not a returned function: Solid discards onMount return values, so these listeners
    // and the ResizeObserver used to survive unmount (the sibling onCleanup above already worked).
    onCleanup(() => {
      window.removeEventListener('resize', resizeChart);
      window.removeEventListener('keydown', handleKeyPress);
      if (resizeObserver && containerRef) {
        resizeObserver.unobserve(containerRef);
        resizeObserver.disconnect();
      }
    });
  });

  createEffect(() => {
    if (!isPanelActive()) return;
    const hasUpdate = triggerUpdate();
    const isReady = untrack(() => mapInitialized() && map);
    const inProgress = untrack(() => updateInProgress);

    if (!hasUpdate || !isReady) return;
    if (inProgress) {
      return;
    }
    setTriggerUpdate(false);
    const fc = filtered().length;
    lastFilteredCount = fc;
    if (fc > 0) {
      filteredWatcherTriggered = true;
    }
    queueMicrotask(() => {
      if (untrack(() => updateInProgress)) return;
      untrack(() => updateMap());
    });
  });

  // Watch for filtered data changes - update map when data becomes available and map is ready
  // This serves as a backup in case triggerUpdate doesn't fire or gets cleared
  // BUT: Only use this if triggerUpdate is NOT set, to avoid duplicate updates
  createEffect(() => {
    if (!isPanelActive()) return;
    const currentFilteredCount = filtered().length;
    const isReady = untrack(() => mapInitialized() && map);
    const hasTriggerUpdate = untrack(() => triggerUpdate());
    
    // Use untrack to check updateInProgress to prevent reactive dependency
    const inProgress = untrack(() => updateInProgress);
    
    // Reset the trigger flag when filtered count goes to 0 (data cleared)
    if (currentFilteredCount === 0) {
      filteredWatcherTriggered = false;
    }
    
    // Only update if filtered count changed from 0 to non-zero (data just loaded)
    // and we haven't already processed this data
    // AND triggerUpdate is not set (let that handle the update instead)
    // AND update is not already in progress
    if (isReady && currentFilteredCount > 0 && lastFilteredCount === 0 && !hasTriggerUpdate && !filteredWatcherTriggered && !inProgress) {
      filteredWatcherTriggered = true;
      // Use untrack to prevent this update from triggering reactive effects
      untrack(() => {
        updateMap();
      });
    }
    
    lastFilteredCount = currentFilteredCount;
  });

  createEffect(() => {
    if (triggerSelection()) {
      // Use untrack to prevent updateSelection from creating reactive dependencies
      untrack(() => {
        updateSelection();
      });
    }
  });

  // Watch for selectedGroupKeys changes (from cross-window sync or table/timeseries) and update selection
  createEffect(() => {
    selectedGroupKeys();
    const isReady = untrack(() => mapInitialized() && map && svg && g);
    
    // Update selection when selectedGroupKeys changes and map is ready
    // Use untrack to prevent updateSelection from creating reactive dependencies
    if (isReady) {
      untrack(() => {
        updateSelection();
      });
    }
  });

  // Watch for groupDisplayMode changes (OFF/ON/MIX) — redraw so hover circles and boats get correct pointer-events and handlers
  // Trigger on actual mode change; also on initial run when mode is MIX so first paint is correct (fix 3)
  let prevGroupDisplayMode: string | undefined;
  createEffect(() => {
    const mode = groupDisplayMode();
    const isReady = untrack(() => mapInitialized() && map);
    const isModeChange = prevGroupDisplayMode !== undefined && prevGroupDisplayMode !== mode;
    const isInitialMIX = prevGroupDisplayMode === undefined && mode === 'MIX';
    if (isReady && (isModeChange || isInitialMIX)) {
      untrack(() => setTriggerUpdate(true));
    }
    prevGroupDisplayMode = mode;
  });

  // Watch for sourcesStore changes when using SOURCE coloring - rebuild color scale when sources load
  createEffect(() => {
    const currentColor = color();
    if (currentColor !== 'SOURCE') {
      return; // Only react when coloring by SOURCE
    }
    
    // Access sourcesStore to trigger effect when sources load
    const sources = sourcesStore.sources();
    const isReady = sourcesStore.isReady();
    
    // If sources just loaded and we have data, rebuild the color scale
    if (isReady && sources.length > 0 && mapdata().length > 0) {
      debug('Map: Sources loaded, rebuilding color scale for SOURCE coloring');
      // Use untrack to prevent InitScales from creating reactive dependencies
      untrack(() => {
        InitScales();
      });
      // Trigger a redraw to apply new colors - use untrack to prevent reactive loop
      const isMapReady = untrack(() => mapInitialized() && map);
      if (isMapReady) {
        untrack(() => {
          updateSelection();
        });
      }
    }
  });

  createEffect(
    on(
      color,
      () => {
        const isReady = untrack(() => mapInitialized() && map && svg && g);
        if (isReady && untrack(() => mapdata().length > 0)) {
          debug('Map: Color changed, rebuilding color scale and updating selection');
          untrack(() => {
            InitScales();
            updateSelection();
          });
        }
      },
      { defer: true }
    )
  );

  onCleanup(() => {
    setMapInitialized(false);
    if (centerTimeout != null) {
      clearTimeout(centerTimeout);
      centerTimeout = null;
    }
    try {
      d3MapPane().select("svg").selectAll("*").remove();
    } catch {
      // SVG might not exist
    }
    if (map) {
      try {
        map.remove();
      } finally {
        map = null;
      }
    }
    if (resizeObserver && containerRef) {
      resizeObserver.unobserve(containerRef);
      resizeObserver.disconnect();
    }

    // Remove window resize event listener
    window.removeEventListener('resize', resizeChart);
    // Note: keydown listener is cleaned up in onMount return function
  });

  return (
    <>
      {firstload() && <Loading />}
      <div ref={(el) => { containerRef = el ?? undefined; }} class="maneuver-map-container" style={{
          "opacity": loading() ? 0.2 : 1, 
          "pointer-events": loading() ? "none" : "auto", 
          "transition": "opacity 0.5s ease",
          "width": "100%",
          "height": "100%",
          "display": "flex",
          "flex-direction": "column",
          "padding-right": "25px",
        }}>
        <div id={maneuverMapElementId()} class="maneuver-map" style={{
            "flex": "1",
            "width": "100%",
            "height": "100%",
          }}></div>
        {/* <p class="flex justify-end mt-2">Lines are separated by 25m</p> */}
        <p class="flex justify-end mt-2">Lines are separated by 100m</p>
        {/* Portalled to <body> — see standard/Map.tsx: a transformed ancestor is the containing block
            for position: fixed descendants, so an in-tree tooltip is offset and scaled. */}
        <Portal>
          <div id="tt" class="tooltip" style={{
              opacity: tooltip().visible ? 1 : 0,
              left: `${tooltip().x}px`,
              top: `${tooltip().y}px`,
              position: "fixed",
              "pointer-events": "none",
              "z-index": 9999,
            }} innerHTML={sanitizeTooltipHtml(tooltip().content)} />
        </Portal>
      </div>
    </>
  );
}
